import type { IntegrationAuthType, IntegrationCapability, IntegrationDefinition, IntegrationId } from '../types/integration.js';

export const INTEGRATION_REGISTRY: readonly IntegrationDefinition[] = [
  {
    id: 'linear',
    name: 'Linear',
    category: 'project-management',
    description: 'Issue tracking and project management',
    supported_auth_types: ['api_key', 'oauth2'],
    capabilities: [
      { id: 'linear:read-issues', name: 'Read Issues', description: 'View issues, projects, and teams', access: 'read' },
      { id: 'linear:create-issue', name: 'Create Issues', description: 'Create and update issues', access: 'write' },
      { id: 'linear:manage-projects', name: 'Manage Projects', description: 'Create/archive projects and manage team settings', access: 'admin' },
    ],
    cli_tool: {
      package: '@schpet/linear-cli',
      binary: 'linear',
      env_key: 'LINEAR_API_KEY',
      skill_id: 'linear-cli',
      extra_env: { LINEAR_ISSUE_SORT: 'priority' },
      installer: 'npm',
    },
  },
  {
    id: 'github',
    name: 'GitHub',
    category: 'code',
    description: 'Source code hosting, pull requests, and CI/CD',
    // CS-1441: `github_app` adds BYO GitHub App installation auth (bot identity,
    // per-repo least-privilege, survives personnel changes) alongside the
    // existing user OAuth + PAT options.
    supported_auth_types: ['api_key', 'oauth2', 'github_app'],
    // ENG-7015: customer-installable native — OAuth-first in the connect UI.
    installable: { category: 'Code', authTypes: ['oauth2', 'api_key', 'github_app'] },
    capabilities: [
      { id: 'github:read-repos', name: 'Read Repositories', description: 'View repos, issues, and PRs', access: 'read' },
      { id: 'github:write-code', name: 'Write Code', description: 'Push commits and create PRs', access: 'write' },
      { id: 'github:manage-repos', name: 'Manage Repositories', description: 'Create/delete repos and manage settings', access: 'admin' },
    ],
    cli_tool: {
      package: 'gh',
      binary: 'gh',
      env_key: 'GITHUB_TOKEN',
      skill_id: 'gh-cli',
      // ENG-6206: `brew` never installs on the Linux fleet (root-on-AL2023,
      // no Homebrew) — gh was permanently missing. Use an OS-detecting script
      // that installs from GitHub's official repos: dnf (AL2023 / RHEL),
      // apt (Debian / Ubuntu), and brew (macOS hosts). The catalog is the
      // trust boundary — this string is source-controlled, never runtime data.
      installer: 'script',
      script:
        'if command -v dnf >/dev/null 2>&1; then curl -fsSL https://cli.github.com/packages/rpm/gh-cli.repo -o /etc/yum.repos.d/gh-cli.repo && dnf install -y gh; elif command -v apt-get >/dev/null 2>&1; then curl -fsSL https://cli.github.com/packages/githubcli-archive-keyring.gpg -o /usr/share/keyrings/githubcli-archive-keyring.gpg && chmod go+r /usr/share/keyrings/githubcli-archive-keyring.gpg && echo "deb [arch=$(dpkg --print-architecture) signed-by=/usr/share/keyrings/githubcli-archive-keyring.gpg] https://cli.github.com/packages stable main" > /etc/apt/sources.list.d/github-cli.list && apt-get update && apt-get install -y gh; elif command -v brew >/dev/null 2>&1; then brew install gh; else echo "gh: no supported installer (need dnf, apt-get, or brew)" >&2; exit 1; fi',
    },
  },
  {
    id: 'google-workspace',
    name: 'Google Workspace',
    category: 'workspace-productivity',
    description: 'Gmail, Calendar, Drive, Sheets, Docs, and Chat',
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'gws:read-email', name: 'Read Email', description: 'Read Gmail messages, threads, and labels', access: 'read' },
      { id: 'gws:send-email', name: 'Send Email', description: 'Send, reply, and forward emails', access: 'write' },
      { id: 'gws:read-calendar', name: 'Read Calendar', description: 'View events and agendas', access: 'read' },
      { id: 'gws:manage-calendar', name: 'Manage Calendar', description: 'Create, update, and delete events', access: 'write' },
      { id: 'gws:read-drive', name: 'Read Drive', description: 'List and download files', access: 'read' },
      { id: 'gws:write-drive', name: 'Write Drive', description: 'Upload, create, and share files', access: 'write' },
      { id: 'gws:read-sheets', name: 'Read Sheets', description: 'Read spreadsheet values', access: 'read' },
      { id: 'gws:write-sheets', name: 'Write Sheets', description: 'Append and update spreadsheet data', access: 'write' },
      { id: 'gws:read-docs', name: 'Read Docs', description: 'Read document content', access: 'read' },
      { id: 'gws:write-docs', name: 'Write Docs', description: 'Create and append to documents', access: 'write' },
      { id: 'gws:chat', name: 'Chat', description: 'Send messages to Google Chat spaces', access: 'write' },
    ],
    cli_tool: {
      package: '@googleworkspace/cli',
      binary: 'gws',
      env_key: 'GOOGLE_WORKSPACE_CLI_TOKEN',
      skill_id: 'gws-cli',
      installer: 'npm',
    },
  },
  {
    id: 'gcloud',
    name: 'Google Cloud SDK',
    category: 'infrastructure',
    description: 'Google Cloud Platform CLI — manage Compute Engine, Cloud Storage, IAM, Cloud Run, Cloud SQL, BigQuery, and Pub/Sub from a single binary',
    supported_auth_types: ['oauth2', 'managed'],
    capabilities: [
      { id: 'gcloud:read', name: 'Read GCP Resources', description: 'List and describe projects, instances, buckets, IAM, and service configs', access: 'read' },
      { id: 'gcloud:write', name: 'Write GCP Resources', description: 'Create and update GCP resources (compute, storage, IAM, run, etc.)', access: 'write' },
      { id: 'gcloud:admin', name: 'Admin GCP Resources', description: 'Destructive operations: delete projects, IAM bindings, instances. Restrict with a guardrail that blocks destructive gcloud/gsutil/bq verbs.', access: 'admin' },
    ],
    cli_tool: {
      package: 'google-cloud-sdk',
      binary: 'gcloud',
      env_key: 'GOOGLE_APPLICATION_CREDENTIALS',
      // gcloud ships as a homebrew cask on macOS (`brew install --cask google-cloud-sdk`)
      // and via curl-installed tarball elsewhere. Neither matches the simple `brew install
      // <package>` or `npm install -g <package>` shape, so leave install to the operator.
      installer: 'manual',
    },
    docs_url: 'https://cloud.google.com/sdk',
  },
  {
    id: 'xero',
    name: 'Xero',
    category: 'accounting',
    description: 'Cloud accounting — financial reports, transactions, and account balances',
    supported_auth_types: ['oauth2'],
    // ENG-7015: customer-installable native.
    installable: { category: 'Accounting', authTypes: ['oauth2'] },
    capabilities: [
      { id: 'xero:read-reports', name: 'Read Reports', description: 'Pull P&L, balance sheet, and trial balance reports', access: 'read' },
      { id: 'xero:read-accounts', name: 'Read Accounts', description: 'View chart of accounts and account balances', access: 'read' },
      { id: 'xero:read-transactions', name: 'Read Transactions', description: 'View bank transactions, invoices, and journal entries', access: 'read' },
      { id: 'xero:read-contacts', name: 'Read Contacts', description: 'View customers, suppliers, and contact groups', access: 'read' },
      { id: 'xero:manage-settings', name: 'Manage Settings', description: 'Manage org settings and chart of accounts', access: 'admin' },
    ],
  },
  {
    id: 'granola',
    name: 'Granola',
    category: 'knowledge',
    description: 'Meeting notes search — query transcripts, summaries, and folders from Granola',
    // Granola uses a remote streamable-HTTP MCP with PKCE + Dynamic Client
    // Registration. End-user OAuth is brokered by the webapp (ENG-4693)
    // through the shared /integrations/oauth/authorize → /callback path
    // (ENG-4694), and the access_token is injected into .mcp.json via the
    // generic bearer-header path. No host-side action required from the
    // operator beyond running the one-time DCR registration script at
    // deploy time.
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'granola:search-meetings', name: 'Search Meetings', description: 'Browse meetings, search content, and chat with notes (query_granola_meetings, list_meetings, get_meetings)', access: 'read' },
      { id: 'granola:read-transcripts', name: 'Read Transcripts', description: 'Access raw meeting transcripts (paid plans only — get_meeting_transcript)', access: 'read' },
      { id: 'granola:list-folders', name: 'List Folders', description: 'View accessible meeting folders (paid plans only — list_meeting_folders)', access: 'read' },
    ],
    docs_url: 'https://docs.granola.ai/docs/api/mcp',
    beta: true,
  },
  {
    id: 'brand-ninja',
    name: 'Brand Ninja',
    category: 'social',
    description: 'Brand-aligned content generation: submit async content requests and track them, search transcripts and assemble ranked timelines from source clips, discover the account catalog (channels, brands, topics, templates, content types, skills), and author catalog entries and knowledge (topics, timeline templates, knowledge items). Wired as the hosted Brand Ninja External-Content MCP at https://ext-api.app.brandninja.ai/v1/mcp.',
    // ENG-6820: same remote streamable-HTTP MCP + OAuth pattern as Granola.
    // Brand Ninja's server implements the full MCP discovery chain (RFC
    // 9728/8414/7591); auth is OAuth 2.0 authorization-code with PKCE (S256)
    // and a public client registered via one-time Dynamic Client Registration
    // (scripts/dcr-register.ts against https://ext-api.app.brandninja.ai/v1/oauth/register
    // → OAUTH_BRAND_NINJA_CLIENT_ID). End-user consent is brokered by the
    // webapp through the shared /integrations/oauth/authorize → /callback path,
    // and the access_token is injected into .mcp.json via the generic
    // bearer-header path (OAUTH_PROVIDERS.brand-ninja.mcpUrl). No host-side
    // action beyond the deploy-time DCR registration.
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'brand-ninja:generate-content', name: 'Generate Content', description: 'Submit async brand-aligned content-generation requests and poll their status (submit_content_request, get_content_status, list_content_requests)', access: 'write' },
      { id: 'brand-ninja:list-channels', name: 'List Channels', description: 'Discover the publishing channels available to the account, metadata only (list_channels)', access: 'read' },
      { id: 'brand-ninja:list-catalog', name: 'List Catalog', description: 'Discover account catalog metadata: brands, topics, timeline templates, content types, and source, output and conversion skills (list_brands, list_topics, list_timeline_templates, list_content_types, list_source_skills, list_output_skills, list_conversion_skills)', access: 'read' },
      { id: 'brand-ninja:search-transcripts', name: 'Search Transcripts', description: 'Search the transcripts available to the account to source or reference when generating content, text and metadata (search_transcripts)', access: 'read' },
      { id: 'brand-ninja:assemble-timeline', name: 'Assemble Timeline', description: 'Turn transcript spans into topic-tagged source clips and rank a topic\'s clips into a ranked timeline via an async LLM job (create_source_clips, create_timeline_ranking)', access: 'write' },
      { id: 'brand-ninja:manage-catalog', name: 'Manage Catalog', description: 'Author catalog entries the other Brand Ninja tools then reference: create a topic or a timeline template (create_topic, create_timeline_template)', access: 'write' },
      { id: 'brand-ninja:manage-knowledge', name: 'Manage Knowledge', description: 'Build the account knowledge base: create a knowledge item and link it to a topic or brand to ground generated content (create_knowledge, link_knowledge)', access: 'write' },
      { id: 'brand-ninja:read-credentials', name: 'Read Credentials', description: 'Read-only External-API credential metadata, secrets stripped. Requires the elevated external-api/admin scope (list_credentials)', access: 'admin' },
    ],
    docs_url: 'https://ext-api.app.brandninja.ai/v1/mcp',
    beta: true,
  },
  {
    id: 'kajabi',
    name: 'Kajabi',
    category: 'crm',
    description: 'Run a Kajabi creator business from chat: read contacts, products, offers, and analytics, manage contact tags & segments, draft email broadcasts & sequences, and update course details & thumbnails.',
    // Same remote streamable-HTTP MCP + OAuth pattern as Granola/Brand Ninja.
    // Kajabi's Doorkeeper AS implements the MCP discovery chain (RFC
    // 9728/8414/7591); auth is OAuth 2.0 authorization-code with PKCE (S256)
    // and a public client registered via one-time Dynamic Client Registration
    // (scripts/dcr-register.ts against https://mcp.kajabi.com/mcp/oauth/register
    // → OAUTH_KAJABI_CLIENT_ID; register with --scope 'read write:contacts
    // write:emails write:content write:commerce' (ENG-7483) since Doorkeeper
    // caps a dynamic client to its registered
    // scopes). End-user consent is brokered by the webapp through the shared
    // /integrations/oauth/authorize → /callback path, and the access_token is
    // injected into .mcp.json via the generic bearer-header path
    // (OAUTH_PROVIDERS.kajabi.mcpUrl). Every Kajabi tool is site-scoped — agents
    // call list_sites/select_site first. No host-side action beyond the
    // deploy-time DCR registration.
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'kajabi:read', name: 'Read & Discover', description: 'List sites and read contacts, products, offers, purchases, and revenue/contacts analytics (list_sites, select_site, search_contacts, get_contact, list_offers, get_offer, search_products, get_revenue_analytics, …)', access: 'read' },
      { id: 'kajabi:contacts', name: 'Manage Contacts', description: 'Create and apply contact tags, and create/update saved contact segments (create_tag, tag_contact, untag_contact, create_segment, update_segment)', access: 'write' },
      { id: 'kajabi:emails', name: 'Manage Emails', description: 'Read and draft email broadcasts and sequences — drafts only, sending stays a human action in Kajabi (create_broadcast, create_sequence, list_broadcasts, get_sequence)', access: 'write' },
      { id: 'kajabi:courses', name: 'Manage Courses', description: 'Read course structure and update course details: title, description, and thumbnail (get_course, update_course). ENG-7483: the course-thumbnail refresh path.', access: 'write' },
    ],
    docs_url: 'https://help.kajabi.com/articles/api-integrations/connect-kajabi-to-claude-or-chatgpt',
    beta: true,
  },
  {
    id: 'anchor-browser',
    name: 'Anchor Browser',
    category: 'workspace-productivity',
    description: 'Cloud browser for agents — drive any website that lacks an API (LinkedIn, Sales Navigator, supplier portals) via a hosted, stealth Chromium with persistent-login profiles. Wired as Anchor\'s HOSTED streamable-HTTP MCP at https://api.anchorbrowser.io/mcp.',
    // ENG-5855: api-key header auth (NOT OAuth, NOT a local stdio package).
    // The manager writes ANCHOR_BROWSER_API_KEY to .env.integrations from the
    // stored api_key credential; the hosted MCP authenticates on the
    // `anchor-api-key` header. The `anchor-session-id` header binds an
    // authenticated profile session — its value is minted per-session by the
    // manager (ENG-5857); until then `envDefaults` seeds it empty so
    // stateless browsing works and no literal `${...}` placeholder ships.
    // Tool surface (25 `anchor_*` tools) is the hosted MCP's, validated in
    // the ENG-5854 spike (docs/spikes/eng-5854-anchor-browser-persistent-login.md).
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'anchor-browser:browse', name: 'Browse & Read', description: 'Navigate and read pages — snapshot, screenshot, page HTML, tabs, console, network requests, wait (anchor_navigate, anchor_snapshot, anchor_take_screenshot, anchor_get_body_html, anchor_tab_list, anchor_console_messages, anchor_network_requests, anchor_wait_for, anchor_navigate_back/forward)', access: 'read' },
      { id: 'anchor-browser:interact', name: 'Interact', description: 'Act on pages — click, type, hover, drag, select options, press keys, handle dialogs, upload files, resize, manage tabs (anchor_click, anchor_type, anchor_hover, anchor_drag, anchor_select_option, anchor_press_key, anchor_handle_dialog, anchor_file_upload, anchor_resize, anchor_tab_new/select/close, anchor_close)', access: 'write' },
      { id: 'anchor-browser:export', name: 'Export & Codegen', description: 'Save the current page as PDF and generate Playwright code for a scenario (anchor_pdf_save, anchor_generate_playwright_code)', access: 'write' },
    ],
    docs_url: 'https://docs.anchorbrowser.io/introduction',
    beta: true,
    remoteMcp: {
      type: 'http',
      url: 'https://api.anchorbrowser.io/mcp',
      // ENG-6993 / ADR-0033: the api-key credential header now goes through the
      // structured `auth` field — the env var (ANCHOR_BROWSER_API_KEY) is
      // DERIVED from this integration's definition_id + credential_ref, so it
      // is scoped to Anchor and can't reference another integration's secret
      // (C1). Renders byte-identically to the previous verbatim header.
      auth: { scheme: 'header', header_name: 'anchor-api-key', credential_ref: 'api_key' },
      // The dynamic session header stays here (not a credential — minted per
      // session by ENG-5857; empty default below until then).
      headers: {
        'anchor-session-id': '${ANCHOR_BROWSER_SESSION_ID}',
      },
      // ENG-5857 mints the real session id; default empty so the header
      // resolves cleanly (no profile bound → ephemeral session) until then.
      envDefaults: { ANCHOR_BROWSER_SESSION_ID: '' },
      // ENG-7748: route through the stdio remote-MCP proxy so the api-key and
      // the minted anchor-session-id headers are read LIVE per request. A
      // re-minted session id then takes effect with no agent respawn (a direct-
      // HTTP header would be frozen at spawn).
      liveHeaderRefresh: true,
    },
  },
  {
    id: 'deck',
    // Display name only (ENG-7861); the catalog id / definition_id stays 'deck'
    // (a committed contract used by agent_integrations, rate cards, the broker,
    // and migrations). "Deck Browser" makes the computer-use purpose obvious.
    name: 'Deck Browser',
    category: 'workspace-productivity',
    description:
      'Computer-use agents that operate any software through its real interface (no API required) and return schema-validated results. A higher-level alternative to Anchor Browser: Deck owns the auth lifecycle (encrypted credential vault, login, MFA, CAPTCHA) and provisions isolated desktop sessions on demand. Ninjafy manages Deck access for you and gives each agent its own isolated Deck workspace, so there is no credential to enter.',
    // Deck is REST-only (base https://api.deck.co/v2, Bearer `sk_live_` account
    // key) — it ships NO MCP server, so unlike anchor-browser there is no
    // `remoteMcp`/`nativeMcp` drop-in; the agent-facing tools are brokered
    // server-side (deck-broker.ts). Deck is the first PREMIUM integration:
    // Ninjafy owns ONE Deck account that every customer agent's runs bill back
    // to (per-org charging is tracked in ENG-6920, not yet live), so the account
    // key is a single platform-held secret (`DECK_ACCOUNT_KEY`), NOT a per-agent
    // credential. Auth type is therefore `none` — customers never enter a key.
    // Per-agent isolation is modelled on Deck's first-class resources: that one
    // key provisions one Deck agent (`agt_`) + vault credential (`cred_`) per
    // Ninjafy agent via POST /:id/provision-deck; the ids land in
    // `agent_integrations.config` (deck_agent_id / deck_credential_id), so
    // revocation + audit happen at the per-agent Deck-resource level without a
    // distinct API key per agent (Deck exposes no key-minting admin API).
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'deck:provision', name: 'Provision Agent Access', description: 'Provision a per-agent Deck agent and vault credential under the account key (create_agent, create_credential)', access: 'admin' },
      { id: 'deck:run', name: 'Run Tasks', description: 'Submit tasks to the agent and read schema-validated structured results (run_task, get_task_run)', access: 'write' },
      { id: 'deck:observe', name: 'Observe Sessions', description: 'Read isolated session state, screenshots, and agent-reasoning artifacts (get_session)', access: 'read' },
    ],
    docs_url: 'https://docs.deck.co/',
    beta: true,
    // ENG-6920: Deck is the first PREMIUM integration. Unlike the customer-auth
    // integrations, every Deck run bills back to Ninjafy's single account
    // key, and Deck is usage-priced, so it is gated on a per-org opt-in and
    // metered. ENG-7032: `meters` declares the billable operations; the priced
    // rate card (integration_rate_cards) holds the amounts. Each event_type must
    // match what deck-broker writes to integration_usage_events.
    // ENG-7864: compute time (run_minutes) is now billed per-org too, in addition
    // to the per-run charge - run_task ($0.35 USD / A$0.50 per run, a modest base)
    // plus run_minutes (per compute-minute, carrying usage margin). The unpriced
    // run_compute (ms) monitor is deliberately NOT a customer meter (fleet ceiling
    // only).
    premium: {
      pricing: 'usage',
      note: 'Billed per Deck task run, plus per compute-minute.',
      meters: [
        { event_type: 'run_task', unit: 'run', label: 'Base run fee' },
        { event_type: 'run_minutes', unit: 'minute', label: 'Compute time' },
      ],
    },
  },
  {
    id: 'browserbase',
    name: 'Browserbase',
    category: 'workspace-productivity',
    description:
      'A hosted browser your agents drive to operate real websites, behind residential proxies and staying signed in via a per-agent encrypted login profile. An operator logs an agent into a site once through a secure live view; every later session reuses that authenticated profile - no credential to enter and the agent never handles the login. A lower-level, self-driven alternative to Deck Browser: Ninjafy manages the Browserbase account for you and gives each agent its own isolated login profile.',
    // Browserbase is REST-only (base https://api.browserbase.com/v1, `X-BB-API-Key`
    // header - NOT Bearer) and ships NO MCP server, so the agent-facing tools are
    // brokered server-side (browserbase-broker.ts). It is a PREMIUM integration on
    // the Deck model (ENG-7947): Ninjafy owns ONE Browserbase account that every
    // customer agent's sessions bill back to, so the account key is a single
    // platform-held secret (`BROWSERBASE_ACCOUNT_KEY`), NOT a per-agent credential.
    // Auth type is `none` - customers never enter a key. Per-agent isolation is a
    // per-agent Context (persistent, per-context-encrypted auth store) provisioned
    // via POST /:id/provision-browserbase; the id lands in
    // `agent_integrations.config.browserbase_context_id`. Browserbase exposes no
    // key-minting admin API, so isolation is on Contexts, not per-agent keys.
    // Unlike Deck (an autonomous task runner), Browserbase is raw browser
    // infrastructure: the broker mints a session and returns a session-scoped
    // connect url the agent drives itself; proxies require a Browserbase paid plan.
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'browserbase:provision', name: 'Provision Agent Access', description: 'Provision a per-agent Browserbase Context (persistent login profile) under the account key', access: 'admin' },
      { id: 'browserbase:run', name: 'Run Browser Sessions', description: 'Open and release proxied browser sessions bound to the agent Context (open_session, release_session)', access: 'write' },
      { id: 'browserbase:observe', name: 'Observe Sessions', description: 'Read session status and proxy usage (get_session)', access: 'read' },
    ],
    docs_url: 'https://docs.browserbase.com/',
    beta: true,
    // ENG-7947: PREMIUM, usage-priced. Every session bills back to Ninjafy's
    // single account key as proxy data + session time, so it is gated on a per-org
    // opt-in and metered. `meters` declares the billable operations; the priced
    // rate card (integration_rate_cards) holds the amounts. Each event_type must
    // match what browserbase-broker writes to integration_usage_events at release
    // (proxy_mb, session_minute).
    premium: {
      pricing: 'usage',
      note: 'Billed per proxy megabyte and per session-minute.',
      meters: [
        { event_type: 'proxy_mb', unit: 'MB', label: 'Proxy data' },
        { event_type: 'session_minute', unit: 'minute', label: 'Session time' },
      ],
    },
    // ENG-7961: the broker returns a session `connect_url`; the AGENT drives the
    // session with the `browse` CDP CLI over Bash (`browse open <url> --cdp
    // <connect_url>`). So the host needs the `browse` binary. The manager's
    // ensureToolkitCli installs it (npm) when a browserbase integration is
    // present for the agent - and it is ALSO baked into the agt-runtime image
    // (Docker-isolated agents don't get host installs mounted in), exactly like
    // the `gh` precedent (ENG-7662).
    //
    // browse needs NO host credential: the agent drives the broker-provisioned
    // session over `connect_url`, and the Browserbase account key stays
    // server-side (never materialized to the agent). `env_key` is a required
    // field but INERT here - auth_type 'none' means no token is ever published
    // under it (the claudecode provisioner guards on `env_key && token`).
    cli_tool: {
      package: 'browse',
      binary: 'browse',
      env_key: 'BROWSERBASE_UNUSED',
      installer: 'npm',
    },
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    category: 'media',
    description:
      'Speech-to-text for inbound voice notes. When a teammate sends an agent a voice message (Slack, Telegram, etc.), the agent uploads the audio and gets back an accurate transcript via ElevenLabs Scribe, so a voice note is no longer a black box. Ninjafy manages ElevenLabs access for you - there is no key to enter.',
    // ElevenLabs is REST-only for our use (POST /v1/speech-to-text, `xi-api-key`
    // header, NOT Bearer) — it ships no MCP server, so the agent-facing tools are
    // brokered server-side (scribe-broker.ts). It is a PREMIUM integration on the
    // Deck model (ADR-0031, epic ENG-6920): Ninjafy owns ONE ElevenLabs account
    // that every customer agent's transcriptions bill back to, so the account key
    // is a single platform-held secret (`ELEVENLABS_ACCOUNT_KEY`), NOT a per-agent
    // credential. Auth type is therefore `none` — customers never enter a key.
    // Usage is metered per operation at the broker chokepoint and gated on a
    // per-org opt-in + monthly cap. (ENG-7556: text-to-speech and music are now
    // standalone brokered tools too - not tied to Augmented Live - sharing the
    // same account key and budget; see the elevenlabs:tts / :music capabilities.)
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'elevenlabs:transcribe', name: 'Transcribe Voice Notes', description: 'Upload an inbound audio file and transcribe it to text via ElevenLabs Scribe (scribe_create_upload, scribe_transcribe)', access: 'write' },
      // ENG-7556: standalone text-to-speech. Surfaced as the general brokered
      // elevenlabs_text_to_speech tool (scribe-broker.ts), available to any agent
      // and no longer tied to Augmented Live; shares this one platform account key
      // + the same per-org budget.
      { id: 'elevenlabs:tts', name: 'Text to Speech', description: 'Synthesize spoken audio (MP3) from text in a chosen voice via ElevenLabs text-to-speech (elevenlabs_text_to_speech)', access: 'write' },
      // ENG-7556: standalone instrumental music generation. Surfaced as the general
      // brokered elevenlabs_generate_music tool (scribe-broker.ts), available to any
      // agent and no longer tied to Augmented Live; shares this one platform account
      // key + the same per-org budget.
      { id: 'elevenlabs:music', name: 'Generate Music', description: 'Compose an instrumental MP3 music track from a text prompt via ElevenLabs Music (elevenlabs_generate_music)', access: 'write' },
      // ENG-7453: sound-effects + speech-to-speech, surfaced as general brokered
      // agent tools (elevenlabs_generate_sound_effect, elevenlabs_speech_to_speech
      // in scribe-broker.ts) available to any agent - not Augmented-Live-only.
      // Both share this one platform account key + the same per-org budget.
      { id: 'elevenlabs:sound_effects', name: 'Generate Sound Effects', description: 'Generate a sound-effect MP3 from a text prompt via ElevenLabs (elevenlabs_generate_sound_effect)', access: 'write' },
      { id: 'elevenlabs:speech_to_speech', name: 'Convert Speech to Speech', description: 'Re-voice an uploaded audio clip in a target voice and return an MP3 via ElevenLabs speech-to-speech (elevenlabs_speech_to_speech)', access: 'write' },
    ],
    // Capabilities index — covers speech-to-text, text-to-speech, music, sound
    // effects, and speech-to-speech, since the integration now advertises
    // elevenlabs:transcribe, elevenlabs:tts, elevenlabs:music,
    // elevenlabs:sound_effects, and elevenlabs:speech_to_speech.
    docs_url: 'https://elevenlabs.io/docs/capabilities',
    beta: true,
    // ENG-10200. ElevenLabs is the mirror image of grok-voice: stt + tts, no
    // realtime. Its STT is easy to overlook because the TTS tool is the one
    // usually discussed, but `ElevenLabsScribeClient.transcribe()` and the
    // `scribe_transcribe` broker tool make it the platform's SECOND speech-to-
    // text provider — on a different unit (`audio_second`) from grok-voice's
    // `audio_hour`, which is why the unit is not restated here.
    //
    // `music`, `sound_effects` and `speech_to_speech` are deliberately NOT
    // modalities. They are audio generation, not a voice conversation, and
    // nothing selecting a voice provider should be offered them.
    voice: {
      stt: { meterEventType: 'transcribe' },
      tts: { meterEventType: 'tts' },
    },
    // ENG-7005 / ENG-7048: premium (billable). Both surfaces bill back to
    // Ninjafy's single account key and are usage-priced, so the integration is
    // gated on a per-org opt-in and metered. The two surfaces share the one
    // `elevenlabs` definition (and so the one per-org monthly budget). Pricing
    // amounts live in integration_rate_cards; this only declares the model.
    premium: {
      pricing: 'usage',
      note: 'Billed on audio transcribed (per second), speech synthesized (per character), music/sound-effects generated and speech-to-speech converted (per second of output audio).',
      // ENG-7032 / ENG-7453: each surface meters its own event in its own physical
      // unit; the matching integration_rate_cards rows price them. Until a rate is
      // seeded, that event prices at 0. Sound-effects and speech-to-speech meter
      // in seconds of OUTPUT audio (derived from the returned MP3 byte size at the
      // fixed 128 kbps output bitrate) at the broker chokepoint.
      meters: [
        { event_type: 'transcribe', unit: 'audio_second' },
        { event_type: 'tts', unit: 'character' },
        { event_type: 'music', unit: 'second' },
        { event_type: 'sound_effects', unit: 'second' },
        { event_type: 'speech_to_speech', unit: 'second' },
      ],
    },
  },
  {
    id: 'grok-voice',
    name: 'Grok Voice',
    category: 'media',
    description:
      'Real-time two-way voice for your agent, powered by xAI Grok. Speak to the agent and hear it reply. Ninjafy manages Grok Voice access for you - there is no key to enter.',
    // Grok Voice is a PREMIUM integration on the platform-key model (mirrors
    // ElevenLabs / ADR-0031): Ninjafy holds one xAI account key and every
    // customer agent's voice minutes bill back to it, so auth_type is `none` -
    // customers never enter a key. Enabling this integration (resolved via the
    // agent/team/org scope chain) is what unlocks voice in Direct Chat; it
    // supersedes the legacy `grok-voice` channel (ENG-7523 spike). The realtime
    // client (webapp Voice Chat tab) and the ephemeral-token mint already exist;
    // this definition adds the premium opt-in + metering gate. Usage meters per
    // voice-minute at the session chokepoint (the mint wiring is a follow-up).
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'grok-voice:converse', name: 'Voice Conversation', description: 'Hold a real-time two-way spoken conversation with the agent (xAI Grok realtime voice)', access: 'write' },
    ],
    docs_url: 'https://docs.x.ai/developers/model-capabilities/audio/voice-agent',
    beta: true,
    // ENG-10200. Grok does realtime AND rest STT, and NO tts — xAI ships no REST
    // text-to-speech endpoint (this tree calls api.x.ai/v1 for /stt,
    // /realtime/client_secrets and /videos only). The absent `tts` is the
    // load-bearing part: it is what stops a caller assembling a speech-in /
    // speech-out round trip out of this provider alone.
    voice: {
      realtime: { meterEventType: 'voice_session', listVoices: true },
      stt: { meterEventType: 'transcription' },
    },
    // Premium (billable): voice minutes bill back to Ninjafy's xAI account key,
    // usage-priced and gated on a per-org opt-in. Pricing amounts live in
    // integration_rate_cards; this only declares the model + meter.
    premium: {
      pricing: 'usage',
      // THE OPT-IN AUTHORISES BOTH METERS, SO THE NOTE NAMES BOTH (ENG-9913).
      // This string is the point of sale — it renders in the add-integration
      // dialog an org admin reads before opting in — and it described only
      // `voice_session` while the same opt-in, and the same $50 pool, had also
      // covered Ninja Notes cloud transcription since ENG-9870. "Of audio"
      // rather than "of meeting" is load-bearing: the two capture sources post
      // independently, so an hour-long meeting on default settings spends two
      // audio-hours (ADR-0063 §2a).
      note:
        'Billed per minute of real-time voice conversation, and per hour of audio for Ninja Notes cloud transcription — both against the same monthly budget.',
      meters: [
        { event_type: 'voice_session', unit: 'minute' },
        // ENG-9870. Ninja Notes cloud transcription, on the SAME definition id
        // and therefore the SAME $50/org pool as voice conversation — Brad's
        // call on 2026-09-03, against a recommendation to separate it. Sharing
        // is by construction rather than by wiring: `evaluateUsageBudget`
        // iterates every event type under the definition.
        //
        // `audio_hour`, not `minute`. The vendor prices per hour and the sell
        // price is per hour, so hours store exactly (50 minor) where minutes
        // would be 0.8333 repeating; `integration_usage_events.units` is
        // `numeric`, so 30 minutes of audio records 0.5 units precisely. This
        // is the answer to ENG-9773 §10.1's open question about whether hours
        // convert to minutes or the vocabulary gains hours.
        //
        // AN AUDIO-HOUR IS NOT A MEETING-HOUR (ENG-9913, Brad, 2026-09-03). The
        // Mac captures system audio and the microphone as two independent
        // pipelines that post separately, and both default on, so the ordinary
        // meeting records two units per wall-clock hour. That is deliberate: the
        // audio-hour is what we buy, since xAI bills on the duration uploaded.
        // Pricing per meeting-hour would have varied the markup with a capture
        // toggle and could only have been built on a client's assertion about
        // how many pipelines were live. See ADR-0063 §2a.
        //
        // THE CHOKEPOINT EXISTS NOW — `POST /notes/transcribe` (ENG-9913)
        // reserves against this meter before the vendor call, from the bytes it
        // received. The rate card still had to land first, in ENG-9870: a
        // billable event with no USD rate makes `evaluateUsageBudget` throw, and
        // on a SHARED pool that would break Voice Chat's budget check too.
        { event_type: 'transcription', unit: 'audio_hour' },
      ],
    },
  },
  {
    id: 'image-gen',
    name: 'Image Generation',
    category: 'media',
    description:
      'AI image generation from a text prompt. An agent passes a prompt to generate_image and gets back a generated raster image, which it can deliver to a chat channel or embed on an Augmented Live page (ENG-7535). Ninjafy manages the model access for you - there is no key to enter. Generation runs through the Vercel AI Gateway (OpenAI gpt-image-2 today). It is a PREMIUM, usage-billed capability: gated on a per-org opt-in and a monthly USD budget, and metered per generated image.',
    // No vendor key to enter: Ninjafy owns the shared Vercel AI Gateway virtual
    // key (AI_GATEWAY_API_KEY) that fronts the image providers, so auth is `none`
    // (customers never enter a key), same as ElevenLabs.
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'image-gen:generate', name: 'Generate Image', description: 'Generate a raster image from a text prompt (generate_image), deliverable to a chat channel or an Augmented Live page', access: 'write' },
    ],
    docs_url: 'https://platform.openai.com/docs/guides/images',
    beta: true,
    // ENG-7472: premium (billable). Image gen bills back to Ninjafy's shared
    // Vercel AI Gateway key and is usage-priced per generated image, so it is gated
    // on a per-org opt-in (isOrgEntitledToPremium) + a per-org monthly USD budget,
    // and metered on integration_usage_events. This budget is PER-ORG, distinct
    // from the gateway key's own global per-key cap. Pricing lives in
    // integration_rate_cards; this only declares the priced model(s).
    //
    // event_type is per MODEL VERSION (not a flat `image`), because model versions
    // price differently (e.g. gpt-image-2 vs a future gpt-image-3 or a Gemini image
    // model). Only a model we actually run AND have seeded a rate card for is
    // declared here; the handler fails CLOSED when the deployed model maps to an
    // undeclared meter, so a version bump without a rate card refuses rather than
    // silently disabling the budget. Adding a model = declare its meter here + seed
    // its rate card + add a normalizer entry in image-generation.ts.
    premium: {
      pricing: 'usage',
      note: 'Billed per generated image; the rate depends on the model/version used.',
      meters: [
        { event_type: 'gpt_image_2', unit: 'image' },
        // GPT Image 2.5 (2026-09-18). Two meters, not one, because the two
        // variants are priced differently on OUR rate card even though OpenAI
        // charges the same per-token rate for both - Sunburst emits far more
        // image-output tokens, and per-image metering has to carry that in the
        // price. Flare holds gpt-image-2's rate (it is the lower-latency
        // successor at the same cost to us); Sunburst is seeded at 4x.
        { event_type: 'gpt_image_2_5_flare', unit: 'image' },
        { event_type: 'gpt_image_2_5_sunburst', unit: 'image' },
      ],
    },
  },
  {
    id: 'video-gen',
    name: 'Video Generation',
    category: 'media',
    description:
      'AI video generation from a text prompt or an input image. An agent passes a prompt (plus optionally image_url) to generate_video and gets back a short generated video with audio, which it can deliver to a chat channel. Ninjafy manages the model access for you - there is no key to enter. Generation runs direct against xAI Grok Imagine: text-to-video on the base grok-imagine-video model, image-to-video on grok-imagine-video-1.5. It is a PREMIUM, usage-billed capability: gated on a per-org opt-in and a monthly USD budget, and metered per second of generated video.',
    // Platform-key model (mirrors Grok Voice / ADR-0031): Ninjafy holds one xAI
    // account key (XAI_API_KEY) and every customer render bills back to it, so
    // auth is `none` - customers never enter a key.
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'video-gen:generate', name: 'Generate Video', description: 'Generate a short video (up to 15s, with audio) from a text prompt (generate_video), deliverable to a chat channel', access: 'write' },
    ],
    docs_url: 'https://docs.x.ai/developers/model-capabilities/video/generation',
    beta: true,
    // Premium (billable): renders bill back to Ninjafy's xAI account key,
    // usage-priced per second of output video, so it is gated on a per-org opt-in
    // (isOrgEntitledToPremium) + a per-org monthly USD budget, and metered on
    // integration_usage_events. Pricing lives in integration_rate_cards; this only
    // declares the priced meters.
    //
    // event_type is per MODEL VERSION x RESOLUTION because xAI prices per output
    // second by resolution (480p vs 720p rates differ). Only a (model, resolution)
    // we actually run AND have seeded a rate card for is declared here; the handler
    // fails CLOSED when the deployed model/resolution maps to an undeclared meter,
    // so a version bump without a rate card refuses rather than silently disabling
    // the budget. Adding a model/resolution = declare its meter here + seed its
    // rate card + add a normalizer entry in video-generation.ts.
    premium: {
      pricing: 'usage',
      note: 'Billed per second of generated video; the rate depends on the resolution.',
      meters: [
        // 1.0 (grok-imagine-video) is the text-to-video model (ENG-7696: xAI's
        // 1.5 is image-to-video ONLY, so t2v runs on the base model).
        { event_type: 'grok_imagine_video_1_0_480p', unit: 'second' },
        { event_type: 'grok_imagine_video_1_0_720p', unit: 'second' },
        { event_type: 'grok_imagine_video_1_5_480p', unit: 'second' },
        { event_type: 'grok_imagine_video_1_5_720p', unit: 'second' },
        // 1080p is image-to-video only (xAI restriction); metered when the
        // agent supplies image_url (ENG-7675).
        { event_type: 'grok_imagine_video_1_5_1080p', unit: 'second' },
      ],
    },
  },
  {
    id: 'postiz',
    name: 'Postiz',
    category: 'social',
    description: 'Open-source social-media scheduling and publishing — schedule posts, list connected platforms, and upload media. Self-hosted-aware (defaults to Postiz Cloud at https://api.postiz.com).',
    // Postiz also supports OAuth2 ('pos_'-prefixed tokens) but the public docs
    // for the authorize/token URL shape are sparse — wired API-key-first; the
    // OAuth path lands as a follow-up once we've confirmed the flow against
    // a live instance.
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'postiz:list', name: 'List Posts & Platforms', description: 'List connected social platforms (GET /integrations) and previously scheduled posts', access: 'read' },
      { id: 'postiz:publish', name: 'Publish Posts', description: 'Create and schedule posts across the connected platforms (POST /posts)', access: 'write' },
      { id: 'postiz:upload', name: 'Upload Media', description: 'Upload images and video for use in posts (POST /upload)', access: 'write' },
    ],
    docs_url: 'https://docs.postiz.com/public-api/introduction',
    // Beta until we've verified the npx-based community MCP server
    // (antoniolg/postiz-mcp) end-to-end against a real Postiz instance.
    // The 30-req/hr public API rate limit also wants real-world
    // validation before we drop the beta flag.
    beta: true,
  },
  {
    id: 'higgsfield',
    name: 'Higgsfield',
    category: 'media',
    description: "Generative media — Soul text-to-image with style presets and trained-character consistency, DoP image-to-video with motion presets, and lip-synced talking video. Uses Higgsfield's REST API with your own API key.",
    // ENG-8440: api_key, NOT the host-brokered MCP this used to be.
    //
    // Higgsfield MCP (`https://mcp.higgsfield.ai/mcp`) cannot be completed from
    // the console, and that is a vendor constraint rather than a gap in our
    // plumbing. Its `oauth-protected-resource` metadata offers two flows and
    // names the clients each is for: the redirect flow lists
    // ["anthropic","claude","claude-ai","claude-code"] and the device-code flow
    // lists ["openclaw","hermes","memoclaw"]. Our console appears in neither,
    // and the device authorization server
    // (`fnf-device-auth.higgsfield.ai/.well-known/oauth-authorization-server`)
    // still returns 404, so there is no discovery document to probe either.
    // The only client Higgsfield permits is Claude Code on the agent's own
    // host, driven by an operator in tmux — no console work removes that step.
    //
    // The REST API (`platform.higgsfield.ai`) has none of those constraints: a
    // static key pair the customer creates in their own Higgsfield account,
    // sent as `Authorization: Key KEY_ID:KEY_SECRET`. That makes this an
    // ordinary paste-a-key integration, and it means WE hold the credential —
    // storable, rotatable, revocable and auditable, none of which was true
    // under the MCP.
    //
    // Bring-your-own-key: generations bill to the customer's own Higgsfield
    // account, so an install is never metered by Ninjafy.
    //
    // NOTE: this entry carries no `remoteMcp`, so it contributes nothing to
    // `registryRemoteMcpKeys` and cannot prune the `higgsfield` key an older
    // host already wrote to `.mcp.json`. That pruning is why `'higgsfield'` is
    // a literal in `integrationDerivedKeys` in the claudecode adapter — do not
    // drop it while any host may still be carrying the retired MCP entry.
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'higgsfield:generate-image', name: 'Generate Image', description: 'Create stills with Soul — style presets, plus trained-character consistency for a repeated face or product.', access: 'write' },
      { id: 'higgsfield:generate-video', name: 'Generate Video', description: 'Animate a still into a short clip with DoP motion presets, or lip-sync a talking video from an image plus audio.', access: 'write' },
      { id: 'higgsfield:read-jobs', name: 'Poll Jobs & Presets', description: 'Poll asynchronous generations to completion, and list the style and motion presets the generate tools accept.', access: 'read' },
    ],
    docs_url: 'https://docs.higgsfield.ai/',
    beta: true,
  },
  {
    id: 'vercel',
    name: 'Vercel',
    category: 'infrastructure',
    description: "Check on Vercel app deployments — what's live, deployment status, and build logs — through Vercel's REST API with your own API token.",
    // ENG-8421: api_key, NOT the host-brokered MCP this used to be.
    //
    // Vercel MCP (`https://mcp.vercel.com`) is not completable from the
    // console, and that is a vendor constraint rather than a gap in our
    // plumbing. Established empirically 2026-08-03: Dynamic Client
    // Registration REJECTS an https redirect (`invalid_redirect_uri` — "not
    // approved for use by this authorization server") and ACCEPTS only
    // localhost; the device-code grant, the one flow needing no redirect, is
    // refused to DCR clients (`unauthorized_client`). So the only client
    // Vercel permits is a local one redirecting to its own ephemeral port —
    // i.e. Claude Code on the agent's host, driven by an operator running
    // `/mcp` in tmux. No console work can remove that step.
    //
    // The REST API has none of those constraints: plain bearer auth
    // (unauthenticated → `{"code":"forbidden","missingToken":true}`, bogus
    // bearer → `{"invalidToken":true}`), so a token the customer creates in
    // their own Vercel dashboard works directly. That makes this an ordinary
    // paste-a-key integration, and it means WE hold the credential — so it can
    // be stored, rotated, revoked and audited, none of which was true when the
    // grant lived in Claude Code's `~/.claude.json` on the host.
    //
    // Tools are declared as data on `integration_definitions.metadata.tools`
    // and dispatch through the existing Direct HTTP broker (the v0 pattern) —
    // there is deliberately no bespoke Vercel client and no bespoke broker.
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'vercel:read-deployments', name: 'Read Deployments', description: 'List teams/projects, check deployment status, and read build logs.', access: 'read' },
    ],
    docs_url: 'https://vercel.com/docs/rest-api',
    // Beta until the REST tool surface has been exercised against a real
    // customer token. (The previous reason — "until the headless OAuth flow is
    // verified end-to-end" — is void: there is no OAuth flow any more.)
    beta: true,
    // NO `remoteMcp`. See the tombstone in the claudecode adapter's prune
    // universe: dropping this field also drops `vercel` from
    // `registryRemoteMcpKeys`, which would strand the `.mcp.json` entry on
    // every host that installed the MCP variant.
  },
  {
    id: 'qmd',
    name: 'QMD Memory Search',
    category: 'knowledge',
    description: 'Local-first memory search sidecar — BM25 + vector search + reranking over agent memory files',
    supported_auth_types: ['none'],
    // ENG-7264: QMD is DEPRECATED - no longer offered in the Add Integration
    // picker (the dialog also drops it via DEPRECATED_PICKER_IDS as a belt-and-
    // suspenders for its DB toolkit row). The entry is retained, sans
    // `installable`, so existing agent installs keep resolving their nativeMcp.
    // Removing `installable` also drops it from the org native allowlist, so new
    // installs are refused at the API too.
    cli_tool: {
      package: '@tobilu/qmd',
      binary: 'qmd',
      env_key: '',
      installer: 'npm',
    },
    capabilities: [
      { id: 'qmd:search', name: 'Search Memory', description: 'Semantic + keyword search over indexed memory files', access: 'read' },
      { id: 'qmd:get', name: 'Get Memory', description: 'Read memory files by path and line range', access: 'read' },
    ],
    beta: true,
    // ENG-5815: migrated from buildMcpJson's hardcoded if-block. qmd is
    // the simplest of the four pre-data-driven entries — no env, no
    // conditional logic, just `qmd mcp`. The byte-identical render is
    // pinned by claudecode-qmd-data-driven.test.ts.
    nativeMcp: {
      command: 'qmd',
      args: ['mcp'],
    },
  },
  {
    // ENG-9244 / CS-1593. Framer is the first `cli_tool` integration whose CLI
    // does NOT authenticate from `env_key`, so read
    // `integrations/framer-credentials.ts` before changing anything here — the
    // env var below serves the SDK, and the CLI is served by a separate
    // `projects.json` the provisioner materialises.
    id: 'framer',
    name: 'Framer',
    category: 'ui-generation',
    description:
      'Design, edit and publish a Framer website — update page copy and images, manage CMS collections, and publish deployments',
    supported_auth_types: ['api_key'],
    // Pre-1.0 on both halves (@framer/agent 0.0.44, framer-api 0.1.29) and
    // Framer bills the Server API itself as a preview. Expect churn.
    beta: true,
    installable: { category: 'Website', authTypes: ['api_key'] },
    capabilities: [
      {
        id: 'framer:read-site',
        name: 'Read Site',
        description: 'Read page text, structure, styles, fonts and CMS content',
        access: 'read',
      },
      {
        id: 'framer:edit-site',
        name: 'Edit Site',
        description:
          'Update page text and images, create pages, manage CMS collections, redirects and localisation',
        access: 'write',
      },
      {
        id: 'framer:publish',
        name: 'Publish',
        description: 'Publish the site and promote a deployment to production',
        access: 'write',
      },
    ],
    cli_tool: {
      package: '@framer/agent',
      // UPSTREAM HAZARD: the package's own bin name is `agent`. On a fleet whose
      // whole job is running agents, putting a global `agent` on PATH is asking
      // for a collision, so the binary we look for and invoke is the shimmed
      // `framer-agent`. `installer: 'script'` (rather than plain npm) is what
      // creates that shim — a bare `npm install -g @framer/agent` would publish
      // `agent` and we would have shipped the collision.
      binary: 'framer-agent',
      // Serves the `framer-api` SDK, which DOES read this. The CLI does not —
      // see framer-credentials.ts for why that distinction matters.
      env_key: 'FRAMER_API_KEY',
      skill_id: 'framer',
      installer: 'script',
      // The catalog is the trust boundary for this string (same contract as the
      // `gh` entry above). Installs into a private prefix and links ONLY the
      // renamed binary, so `agent` never lands on PATH.
      // VERSION IS PINNED ON PURPOSE. `framer-credentials.ts` mirrors this
      // CLI's pre-1.0 `projects.json` schema (already at version 2, with a
      // legacy credentials.json path still in its source — this format HAS
      // churned). An unpinned global install would let a future release land a
      // schema we do not write, leaving the CLI unauthenticated on a headless
      // host. Bump this only alongside a compatibility check of the config
      // format against the new release.
      script:
        'npm install -g --prefix /usr/local/framer-agent @framer/agent@0.0.44 && ln -sf /usr/local/framer-agent/bin/agent /usr/local/bin/framer-agent',
    },
    docs_url: 'https://www.framer.com/developers/server-api-quick-start',
  },
  {
    id: 'v0',
    name: 'v0 by Vercel',
    category: 'ui-generation',
    description: 'Programmatic UI generation — generate React + Tailwind + shadcn/ui components and full apps from natural language prompts',
    supported_auth_types: ['api_key'],
    beta: true,
    capabilities: [
      {
        id: 'v0:generate-ui',
        name: 'Generate UI',
        description: 'Create React components and full apps from a natural language prompt',
        access: 'write',
        required_scopes: ['chats:create'],
      },
      {
        id: 'v0:iterate-ui',
        name: 'Iterate UI',
        description: 'Send follow-up prompts to refine a previously generated component',
        access: 'write',
        required_scopes: ['chats:send'],
      },
      {
        id: 'v0:read-chats',
        name: 'Read Chats',
        description: 'Retrieve chat history, generated files, and demo URLs',
        access: 'read',
        required_scopes: ['chats:read'],
      },
      {
        id: 'v0:manage-projects',
        name: 'Manage Projects',
        description: 'Create and manage v0 project containers for versioned generation history',
        access: 'write',
        required_scopes: ['projects:write'],
      },
      {
        id: 'v0:deploy',
        name: 'Deploy to Vercel',
        description: 'Deploy a generated version to Vercel and receive a live URL',
        access: 'write',
        required_scopes: ['deployments:create'],
      },
    ],
    docs_url: 'https://v0.dev/docs/api/platform/overview',
  },
  {
    id: 'pika',
    name: 'Pika',
    category: 'media',
    description: 'AI video meeting agent — join Google Meet and Zoom calls with a custom avatar and cloned voice via PikaStreaming',
    supported_auth_types: ['api_key'],
    // ENG-7015: customer-installable native — the one straggler not in the DB
    // catalog, now carried by this single shared source.
    installable: { category: 'Media', authTypes: ['api_key'] },
    capabilities: [
      { id: 'pika:join-meeting', name: 'Join Meeting', description: 'Join a video meeting as an AI participant with avatar and voice', access: 'write' },
      { id: 'pika:leave-meeting', name: 'Leave Meeting', description: 'Leave an active video meeting session', access: 'write' },
      { id: 'pika:generate-avatar', name: 'Generate Avatar', description: 'Generate an AI avatar image for video calls', access: 'write' },
      { id: 'pika:clone-voice', name: 'Clone Voice', description: 'Clone a voice from an audio recording', access: 'write' },
    ],
    cli_tool: {
      package: 'pika-skills',
      binary: 'python3',
      env_key: 'PIKA_DEV_KEY',
      skill_id: 'pikastream-video-meeting',
      // python3 is part of the host bootstrap baseline — skills are fetched
      // separately. Don't try to auto-install python via npm/brew.
      installer: 'manual',
    },
    docs_url: 'https://github.com/Pika-Labs/Pika-Skills',
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    category: 'code',
    description: 'Claude Code AI agent runtime — code editing, task execution, file management, and development workflows',
    supported_auth_types: ['api_key', 'none'],
    capabilities: [
      { id: 'claude-code:edit-code', name: 'Edit Code', description: 'Read, write, and edit source files', access: 'write' },
      { id: 'claude-code:run-tasks', name: 'Run Tasks', description: 'Execute bash commands and development tasks', access: 'write' },
      { id: 'claude-code:search', name: 'Search Code', description: 'Search files and grep codebase', access: 'read' },
      { id: 'claude-code:git', name: 'Git Operations', description: 'Commit, branch, push, and manage version control', access: 'write' },
    ],
    cli_tool: {
      package: '@anthropic-ai/claude-code',
      binary: 'claude',
      env_key: 'ANTHROPIC_API_KEY',
      // Claude Code is installed by the host bootstrap / operator setup —
      // don't attempt a second install from the manager poll.
      installer: 'manual',
    },
    docs_url: 'https://docs.anthropic.com/en/docs/claude-code',
  },
  {
    id: 'xurl',
    name: 'xurl (X API)',
    category: 'social',
    description: "Official X (Twitter) API CLI — a curl-like tool for X's REST and streaming endpoints with OAuth 2.0 PKCE, OAuth 1.0a, and bearer-token auth",
    supported_auth_types: ['api_key'],
    // ENG-7015: customer-installable native. The connect UI also offers a
    // keyless "none" option (run against the app's bearer token) that the
    // runtime capability set above does not enumerate.
    installable: { category: 'Social', authTypes: ['none', 'api_key'] },
    capabilities: [
      { id: 'xurl:read', name: 'Read X API', description: 'Call GET endpoints (users, tweets, timelines, search)', access: 'read' },
      { id: 'xurl:write', name: 'Write X API', description: 'Post tweets, reply, like, and retweet', access: 'write' },
      { id: 'xurl:stream', name: 'Stream X API', description: 'Consume filtered and sampled stream endpoints', access: 'read' },
      { id: 'xurl:media', name: 'Upload Media', description: 'Chunked upload of images and video to the X media endpoints', access: 'write' },
    ],
    cli_tool: {
      package: '@xdevplatform/xurl',
      binary: 'xurl',
      env_key: 'X_BEARER_TOKEN',
      skill_id: 'xurl-cli',
      // ENG-6897: was `manual`, on the belief that xurl is only distributed
      // through a homebrew tap. It is not: xdevplatform publish
      // `@xdevplatform/xurl` to npm (1.3.1 at time of writing) with
      // `bin: { xurl: 'cli.js' }`, and the `package` above was already the
      // right one — only the installer disagreed. `manual` meant the manager
      // logged "operator must install" to a host log nobody reads and marked
      // the toolkit permanently ensured, so every agent granted this
      // integration got credentials, a skill, and no binary, silently.
      //
      // The npm package's postinstall downloads the matching release archive
      // from GitHub and untars it, so this needs egress to
      // github.com/.../releases. If that fails, `ensureToolkitCli`'s
      // post-install `which` catches it, logs the child's real stderr and
      // retries with backoff — i.e. the worst case of trying is the state we
      // are already in, with a better log line.
      installer: 'npm',
    },
    docs_url: 'https://github.com/xdevplatform/xurl',
  },
  {
    id: 'coderabbit',
    name: 'CodeRabbit',
    category: 'code',
    description: 'AI-powered code review CLI for local and pre-push review runs',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'coderabbit:review', name: 'Review Changes', description: 'Run a local CodeRabbit review over staged or branch changes', access: 'read' },
    ],
    cli_tool: {
      package: '',
      binary: 'coderabbit',
      env_key: '',
      installer: 'script',
      script: 'curl -fsSL https://cli.coderabbit.ai/install.sh | sh',
    },
    docs_url: 'https://www.coderabbit.ai/cli',
  },
  {
    id: 'aws',
    name: 'AWS',
    category: 'infrastructure',
    description: "Amazon Web Services — query AWS APIs (EC2, S3, IAM, Lambda, etc.) via AWS Labs' official AWS API MCP server",
    supported_auth_types: ['api_key', 'managed', 'none'],
    capabilities: [
      { id: 'aws:read', name: 'Read AWS Resources', description: 'List and describe AWS resources across services (EC2, S3, IAM, Lambda, …)', access: 'read' },
      { id: 'aws:write', name: 'Write AWS Resources', description: 'Create and update AWS resources. Pair with an aws-no-destructive-ops guardrail.', access: 'write' },
    ],
    docs_url: 'https://github.com/awslabs/mcp/tree/main/src/aws-api-mcp-server',
    beta: true,
    // ENG-5815: first integration shipped purely via the data-driven
    // path — buildMcpJson never grew an `aws` if-block. The AWS Labs
    // AWS API MCP server runs through uvx (Python tooling), which the
    // host bootstrap installs alongside python3. Credentials are
    // resolved via the standard AWS_* env / shared credentials file
    // chain on the host; the spec doesn't override them.
    nativeMcp: {
      command: 'uvx',
      args: ['awslabs.aws-api-mcp-server@latest'],
      env: {
        AWS_REGION: '{{empty_if_no_env.AWS_REGION}}',
        AWS_PROFILE: '{{empty_if_no_env.AWS_PROFILE}}',
        PATH: '{{process_env.PATH}}',
        HOME: '{{process_env.HOME}}',
      },
    },
  },
  {
    // ENG-10133 (epic ENG-10127). Drives a HUMAN'S macOS desktop — clicks,
    // keystrokes, window state — through `open-computer-use` running on that
    // Mac, reached over an SSH reverse tunnel the Mac itself opens.
    //
    // READ THIS BEFORE CHANGING ANY FIELD BELOW. Three properties are what make
    // this safe to ship at all, and each is easy to undo by accident:
    //
    //  1. Every forwarded call is gated per-action against a human-granted
    //     approval (ENG-10132, packages/mcp/src/computer-use-gate.ts). The gate
    //     lives INSIDE the proxy binary rather than beside it, so there is no
    //     build in which the tools exist and the gate does not.
    //  2. The `open-computer-use` FLAG_REGISTRY flag decides whether the row is
    //     forwarded to the host at all, and it is `sensitive` — arming it costs
    //     a confirmation. The flag governs whether the PATH EXISTS; the gate
    //     governs whether a call may travel it. Neither substitutes for the other.
    //  3. `beta` keeps it off every customer's picker until the operator runbook
    //     (ENG-10135) exists. Setting up the Mac side — launchd tunnel, the
    //     `from="127.0.0.1,::1"` pin on authorized_keys, Accessibility
    //     permissions — is not something a connect dialog can do, and offering
    //     it without those steps produces an integration that installs cleanly
    //     and does nothing.
    //
    // NOT a `nativeMcp` spec: the entry needs a host-absolute proxy path and
    // per-install ssh config (user, port, remote command), neither of which the
    // ENG-5815 templating vocabulary can express. Rendered by
    // provisioning/computer-use-mcp.ts instead — see its header.
    id: 'open-computer-use',
    name: 'Computer Use (macOS)',
    category: 'infrastructure',
    description:
      'Drive a macOS desktop by accessibility tree — read window state, click, type and press keys in any app. Every action requires a human approval granted per session and bound to one application.',
    // The credential is an OpenSSH private key, supplied by the operator who
    // set up the Mac. `api_key` is this codebase's "customer pastes a secret"
    // flow; there is no ssh-specific auth type and inventing one would buy
    // nothing the credential pipeline does not already do.
    supported_auth_types: ['api_key'],
    beta: true,
    // DELIBERATELY NOT `installable`, i.e. never in the Add Integration picker.
    // It joins augmented-admin / augmented-support / augmented-help-kb as a
    // GRANTED integration, and for a stronger version of their reason. A picker
    // entry promises self-service, and there is none to offer: the Mac side
    // needs a launchd tunnel, an authorized_keys entry pinned to loopback, an
    // SSH keypair and macOS Accessibility permission, none of which a connect
    // dialog can do. A customer who "installed" it from a picker would get a
    // dialog, paste something, and own a broken integration.
    //
    // It would also contradict the flag. `open-computer-use` is `sensitive`, so
    // arming it costs a deliberate confirmation — offering one-click install of
    // the thing behind it is incoherent.
    capabilities: [
      {
        id: 'open-computer-use:read-screen',
        name: 'Read Screen State',
        description:
          'List running applications and read a window\'s accessibility tree (element indices, labels, values). Screenshots are stripped by default and returned only on explicit request.',
        access: 'read',
      },
      {
        id: 'open-computer-use:control',
        name: 'Control the Desktop',
        description:
          'Open apps, click, scroll, type text, press keys and set field values. Every call is refused unless a human has granted an approval for the target application.',
        access: 'write',
      },
    ],
    docs_url: 'https://github.com/anthropics/open-computer-use',
  },
  {
    // ENG-6195: admin-only debugging surface for Integrity Labs STAFF agents.
    // Provisions the @integrity-labs/augmented-admin-mcp stdio broker, which
    // reads end-user agent diagnostics cross-org via /admin/debug/*. `beta` so
    // it is visible/enable-able only by admin-email-domain users; the API
    // double-gates every call on the caller's owning org `is_internal = true`.
    // auth `none` — no end-user OAuth; the host JWT (org_id claim) is the
    // credential. NOT a customer integration; do not promote to `published`.
    id: 'augmented-admin',
    name: 'Ninjafy Admin Debug',
    // ENG-8804: Staff-only cross-org debug surface: never customer-requestable.
    platform_internal: true,
    category: 'infrastructure',
    description: 'Integrity Labs staff-only: cross-org agent/host/integration/alert diagnostics for troubleshooting managed agents.',
    supported_auth_types: ['none'],
    beta: true,
    capabilities: [
      { id: 'augmented-admin:read-diagnostics', name: 'Read Diagnostics', description: 'Cross-org read of agent, host, integration, and alert diagnostics (projection only — never credentials or transcripts).', access: 'read' },
    ],
  },
  {
    // ENG-7023 (ADR-0031/0032): the per-org self-troubleshoot surface for the
    // `system_support` concierge agent. Provisions the
    // @integrity-labs/augmented-support-mcp stdio broker, which reads the
    // agent's OWN org diagnostics and proposes self-remediation writes
    // (create_agent) through the server-rendered HITL approval gate, all via
    // /host/support/*. `beta` while the concierge rolls out gradually (ENG-6975);
    // auth `none` - no end-user OAuth, the host JWT (org_id claim) is the
    // credential and the org-lock. NOT a customer-selectable integration: it is
    // attached automatically to system_support agents at provisioning.
    id: 'augmented-support',
    name: 'Ninjafy Support',
    // ENG-8804: Attached automatically to system_support agents at provisioning.
    platform_internal: true,
    category: 'infrastructure',
    description: "Per-org self-troubleshoot concierge: reads your org's agents, hosts, integrations, alerts, flags, and audit log, files support/feature requests, and proposes new agents for human approval - all scoped to your own organization.",
    supported_auth_types: ['none'],
    beta: true,
    capabilities: [
      { id: 'augmented-support:read-diagnostics', name: 'Read Diagnostics', description: "Read your own org's agents, hosts, integrations, alerts, flags, and audit log (projection only - never credentials or transcripts).", access: 'read' },
      { id: 'augmented-support:file-requests', name: 'File Requests', description: 'File bug / feature / integration requests to Ninjafy support.', access: 'write' },
      { id: 'augmented-support:propose-writes', name: 'Propose Self-Remediation', description: 'Propose creating an agent in your own org; executed only after a human approves a server-rendered diff.', access: 'write' },
    ],
  },
  {
    // ENG-8332: the Ninjafy HELP knowledge base, split out of
    // `augmented-support` so an agent can be given the docs WITHOUT also being
    // given a ticket-filing button, an agent-proposal button, and org-wide read
    // of agents, hosts, integrations, alerts, flags and the audit log. That
    // bundling was why "just give everyone augmented-support" was the wrong
    // answer, and the cost was agents answering platform questions from
    // inference instead of documentation (the CS-1529/CS-1530 case).
    //
    // Provisions the @integrity-labs/augmented-help-kb-mcp stdio broker, a
    // read-only client over /host/kb - the single enumerated carve-out from the
    // ADR-0032 org-lock invariant (ENG-7119). KB content is platform-curated
    // and org-neutral, so this grants NO view of any org's state; the client
    // has no POST transport at all, so writes are structurally impossible
    // rather than merely unregistered.
    //
    // auth `none` - no end-user OAuth, the host JWT is the credential. `beta`
    // while distribution widens. NOT in the Add Integration picker (no
    // `installable`): granted deliberately, like its two siblings.
    //
    // NAMING (ENG-7281): this is the "help knowledge base" - shared, published,
    // org-neutral PRODUCT help. It is NOT the org knowledge store that
    // `knowledge_search` / `context_search` read. Keep the two distinct in every
    // surface that names them.
    id: 'augmented-help-kb',
    name: 'Ninjafy Help KB',
    // ENG-8804: Granted centrally (ENG-8542 auto-grant), never connected by a human.
    platform_internal: true,
    category: 'knowledge',
    description:
      "The Ninjafy help knowledge base: search and read the shared, published product documentation (how-tos, common fixes, FAQs). Identical for every organization and org-neutral by design - it grants no view of your org's own state, and cannot file tickets or propose changes. Distinct from your organization's own knowledge store.",
    supported_auth_types: ['none'],
    beta: true,
    capabilities: [
      {
        id: 'augmented-help-kb:read',
        name: 'Read Help Docs',
        description:
          'Search and read published Ninjafy help articles (org-neutral product documentation). Read-only; no ticket filing, no agent proposals, no org-state reads.',
        access: 'read',
      },
    ],
  },
  {
    id: 'firecrawl',
    name: 'Firecrawl',
    category: 'knowledge',
    description:
      'Web data for agents: scrape, map, crawl and search any website for clean structured data, plus scheduled change-detection monitors whose updates arrive in the agent\'s direct-chat. Connect via the platform-managed account.',
    // ENG-7217: Firecrawl migrates from a customer-API-key stdio MCP
    // (`npx firecrawl-mcp`, FIRECRAWL_API_KEY) to a PREMIUM, platform-managed
    // integration on the Deck / ElevenLabs model (ADR-0031, epic ENG-6920):
    // Ninjafy holds ONE Firecrawl account key (`FIRECRAWL_ACCOUNT_KEY`) that
    // every customer agent's web-data calls bill back to. The agent-facing tools
    // are brokered server-side (firecrawl-broker.ts, reusing the official
    // @mendable/firecrawl-js SDK) rather than via the stdio MCP, so managed usage
    // is metered at the one control-plane chokepoint and gated on a per-org opt-in
    // + monthly USD cap.
    //
    // ENG-8304 / ADR-0055 (Phase 1b — Firecrawl is the BYO exemplar): `api_key`
    // was also offered so a customer could bring their OWN Firecrawl key. A BYO
    // install (`credential_source='byo'`) authenticates with the customer's key
    // (stored encrypted on the install) and is NOT metered — its upstream cost is
    // on the customer's own Firecrawl bill.
    //
    // ENG-9235: `api_key` is PARKED, not abandoned. BYO is gated on the plan-tier
    // `byo_credentials` entitlement, which only `enterprise` has (the column is
    // NOT NULL DEFAULT false, and ENG-9143 explicitly set it false for Team). The
    // connect wizard never reads that flag, so offering `api_key` here sent every
    // Team/Business customer to firecrawl.dev for a key we would then reject with
    // a 403 `byo_not_entitled_by_plan` — after they had signed up for it.
    //
    // Removing it is also what removes that screen: the wizard's picker only
    // renders for 2+ selectable auth types (`selectableWizardAuthMethods`), so a
    // single `['none']` restores the friction-free managed install with no UI
    // change. Restore `api_key` only together with an entitlement-aware wizard —
    // see ENG-9236 for the proper fix.
    //
    // KEEP IN LOCKSTEP with the `firecrawl` row in
    // packages/supabase/seeds/toolkit-definitions.json: that seed carries its own
    // `auth_types` and is what `db:catalog:seed` upserts on every prod deploy, so
    // editing this array alone changes nothing a customer can see.
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'firecrawl:scrape', name: 'Scrape Pages', description: 'Fetch a single URL as clean markdown / structured data (firecrawl_scrape).', access: 'write' },
      { id: 'firecrawl:search', name: 'Web Search', description: 'Search the web and return ranked results, optionally scraped (firecrawl_search).', access: 'write' },
      { id: 'firecrawl:map', name: 'Map Site', description: 'Discover the URLs on a site without scraping them (firecrawl_map).', access: 'read' },
      { id: 'firecrawl:crawl', name: 'Crawl Site', description: 'Crawl a site to many pages; start a job and poll it (firecrawl_crawl, firecrawl_crawl_status).', access: 'write' },
      { id: 'firecrawl:monitor', name: 'Monitor Changes', description: 'Create / list / delete scheduled change-detection monitors delivered into direct-chat (firecrawl_monitor_*).', access: 'write' },
    ],
    docs_url: 'https://docs.firecrawl.dev/',
    beta: true,
    // ENG-7217: premium (billable). Firecrawl bills internally in credits and we
    // pay per credit on the one account key, so the integration is usage-priced,
    // gated on a per-org opt-in, and metered per operation at the broker
    // chokepoint. `meters` declares WHAT is metered and in WHICH physical unit
    // (per Brad's per-operation decision); the priced rate card
    // (integration_rate_cards) holds the per-unit amount. Each event_type must
    // match what firecrawl-broker writes to integration_usage_events. Until a
    // rate row is seeded an event prices at 0 (budget gate inert).
    premium: {
      pricing: 'usage',
      note: 'Billed per page scraped, page crawled, URL mapped, search query, and monitor check.',
      // NOTE: Firecrawl's /extract endpoint is in maintenance mode (deprecated in
      // @mendable/firecrawl-js), so the extract tool + its meter are intentionally
      // not shipped in v1. Add an `extract` meter here if/when a non-deprecated
      // extraction path is exposed.
      meters: [
        { event_type: 'scrape', unit: 'page' },
        { event_type: 'crawl', unit: 'page' },
        { event_type: 'map', unit: 'url' },
        { event_type: 'search', unit: 'query' },
        { event_type: 'monitor_check', unit: 'check' },
      ],
    },
  },
  {
    id: 'origami',
    name: 'origami.chat',
    category: 'crm',
    description:
      'Lead data + outbound: build and enrich lead lists, drive origami\'s AI worker from a natural-language brief, and read the tables it builds. Available on the platform-managed account, or with your own origami key.',
    // ENG-7423: origami migrates from a customer-key-only integration to a
    // PREMIUM one that ALSO keeps BYO — the Firecrawl / ADR-0055 model, with one
    // structural difference that drives most of the design.
    //
    // Firecrawl's premium tier is a single platform account key that every
    // customer's calls bill back to, and isolation is not a concern because a
    // scrape has no tenancy. origami is the opposite: it stores the customer's
    // LEAD LISTS, tables, chats and campaigns. Putting every customer on one
    // parent org would make every customer's data mutually readable, so the
    // managed tier gives each ORGANIZATION its own origami project (child org)
    // and mints a key BOUND to that project (`POST /account/keys` with
    // `project_id`, v3). See `packages/core/src/origami/provision.ts` for why a
    // bound key rather than the `x-origami-project` header — in short, the
    // header is a per-call assertion and the tool argument is deliberately
    // nullable, so under a shared parent an ordinary tool call could reach it.
    //
    // ── auth types, and the ENG-9235 trap this deliberately re-enters ─────────
    // Both are offered, unlike Firecrawl, which PARKED `api_key` down to
    // `['none']` because a 2+-auth-type wizard renders a picker that is not
    // entitlement-aware: a customer picks BYO, goes and fetches a vendor key,
    // and is then rejected with `byo_not_entitled_by_plan` (BYO on a premium def
    // needs `plans.byo_credentials`, enterprise-only today).
    //
    // origami cannot take that exit. BYO is the INCUMBENT here — every existing
    // install is a customer's own key — so dropping `api_key` would remove the
    // only mode its current customers use. The known gap is therefore accepted
    // and pointed at its owner: the picker will still let a non-entitled org
    // choose BYO and reject it afterwards. ENG-9236 (entitlement-aware wizard)
    // is the fix; until it lands the rejection is at least loud, specific, and
    // ahead of any charge.
    //
    // EXISTING installs are unaffected by that gate: the premium migration
    // backfills them to `credential_source='byo'` AND stamps
    // `config.byo_grandfathered_at`, which the call-time resolver honours in
    // place of the plan check. Grandfathering is per-install and set only by
    // that one migration — a NEW BYO install still needs the entitlement.
    //
    // KEEP IN LOCKSTEP with the `origami` rows in
    // packages/supabase/seeds/integration-definitions.json and
    // toolkit-definitions.json: those seeds carry their own auth types and are
    // what `db:catalog:seed` upserts on every prod deploy, so editing this array
    // alone changes nothing a customer can see.
    supported_auth_types: ['none', 'api_key'],
    capabilities: [
      { id: 'origami:read', name: 'Read Lists & Runs', description: 'Discover projects and tables, read built tables and rows, poll runs/batches, and check credit balance. Credit-free.', access: 'read' },
      { id: 'origami:write', name: 'Enrich Leads', description: 'Insert raw rows into an enrichment table (submit_enrichment). Consumes credits.', access: 'write' },
      { id: 'origami:agent', name: 'Drive the AI Worker', description: 'Create an origami agent, send follow-up runs, cancel a run, archive an agent. Consumes credits.', access: 'write' },
    ],
    docs_url: 'https://docs.origami.chat/v3/overview',
    // ENG-7423: premium (billable). origami bills internally in CREDITS, so the
    // integration is usage-priced on one meter whose unit is the vendor's own
    // billing unit. Priced at 2.0 c USD / 3.5 c AUD per credit by
    // `20260907000050_origami_credits_rate_card.sql`, against a retail cost of
    // 1.29-1.43 c/credit depending on which origami tier we hold.
    //
    // The note deliberately does NOT say "at cost". origami sells a
    // SUBSCRIPTION WITH AN INCLUDED BUNDLE while this meters PER CREDIT
    // CONSUMED, so a per-credit price recovers the subscription only at full
    // utilisation — around 72% of the bundle at this rate. Calling that "at
    // cost" in a customer-facing pricing string would be a claim about our
    // margin that is only true at one utilisation level we do not control.
    //
    // ONE meter, not one per tool, because of where the number comes from. Every
    // other premium integration meters at a server-side broker chokepoint, which
    // sees each call and can attribute it. origami's agent surface is a
    // host-resident stdio MCP server (ADR-0040) that calls origami.chat DIRECTLY
    // — the control plane never sees the tool call, so there is no per-operation
    // quantity to meter. What the control plane CAN see is the project's own
    // spend (`GET /account/projects/{id}` → `usage.spent`), which the credit
    // reconciler polls and meters as a delta. That is a per-project total, so it
    // can only ever be one meter; splitting it per tool would require the vendor
    // to break spend down by operation, which v3 does not expose.
    //
    // `unit_label` is set because the machine unit is the vendor's internal
    // billing unit rather than a thing the customer asked for — "(per credit)"
    // is the honest rendering, in the ADR-0053 cost-pass-through spirit.
    //
    // Only a MANAGED install is metered. A BYO install spends the customer's own
    // origami credits on their own bill (`isBilledInstall` → false), which is why
    // the reconciler polls only projects it provisioned.
    premium: {
      pricing: 'usage',
      note: 'Billed per origami credit the agent consumes. Bring your own origami key instead and usage is billed to you by origami directly.',
      meters: [{ event_type: 'credits', unit: 'credit', label: 'origami credits', unit_label: 'credit' }],
    },
  },
  {
    id: 'ayrshare',
    name: 'Ayrshare',
    category: 'social',
    description:
      "Read and report on your social media - post history, analytics, and connected-account status - across LinkedIn, Instagram, Facebook, TikTok, YouTube, Pinterest, and more. Ninjafy manages Ayrshare access for you - there is no key to enter; connect your social accounts once. Publishing on your behalf ships in a later release.",
    // ENG-7722 / ADR-0046: Ayrshare is a PREMIUM, platform-managed integration on
    // the Deck / Firecrawl model (ADR-0031, epic ENG-6920): Ninjafy owns ONE
    // Business Plan account (`AYRSHARE_API_KEY`) and every customer agent's profile
    // + posts bill back to it, so the key is a single platform-held secret, NOT a
    // per-agent credential. Auth type is therefore `none` - customers never enter a
    // key. Per-agent isolation is Ayrshare's own User Profile model: the account
    // key mints one profile per agent (ayrshare-provision.ts), whose profileKey is
    // injected server-side by the broker (ayrshare-broker.ts) so usage is metered +
    // gated at the one control-plane chokepoint. Agent-scoped only (a profile is
    // the per-agent isolation unit).
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'ayrshare:read', name: 'Read Posts & Analytics', description: 'List post history, read a post, and pull social analytics + connected-profile status (ayrshare_history, ayrshare_get_post, ayrshare_analytics, ayrshare_profiles).', access: 'read' },
      { id: 'ayrshare:publish', name: 'Publish Posts', description: 'Publish / schedule / delete social posts across connected networks. Gated by human approval (ADR-0004). Ships in a later slice.', access: 'write' },
    ],
    docs_url: 'https://www.ayrshare.com/docs/',
    beta: true,
    // ADR-0046: premium (billable). Unlike the usage-priced premiums (Deck,
    // Firecrawl), Ayrshare's true cost is a SUBSCRIPTION - USD $7.99 per active
    // User Profile per month - so it is priced on the same axis: a flat monthly
    // fee per agent, billed only for an ACTIVE profile (>=1 linked social
    // account). Per the PremiumDescriptor contract, a monthly premium carries NO
    // per-operation `meters` (it meters nothing per call), so it has no rate-card
    // rows; ENG-7907 puts the per-currency amount on `monthlyPrice` (the single
    // machine source the acknowledgement modal renders and the deferred Stripe
    // billing slice reads). The descriptor also arms the per-org opt-in
    // entitlement gate (isPremiumDefinition / isOrgEntitledToPremium).
    premium: {
      pricing: 'monthly',
      // ENG-7907: USD $15 / AUD $20 per active social profile per month.
      monthlyPrice: { usdPriceMinor: 1500, audPriceMinor: 2000 },
      note: 'Billed USD $15/month (A$20) per active social profile (an agent with at least one connected social account).',
    },
  },
  {
    // ENG-7439: X Search is a PREMIUM, platform-managed integration on the
    // Deck / Firecrawl / ElevenLabs model (ADR-0031, epic ENG-6920): Ninjafy
    // holds ONE X (Twitter) developer app whose OAuth2 app-only bearer
    // (X_APP_BEARER_TOKEN) every customer agent's search bills back to, so the
    // key is a single platform-held secret, NOT a per-agent credential. Auth
    // type is `none` - customers never enter a key. The one read tool runs on
    // the Direct-HTTP broker lane (integration-broker-agent-tools.ts, seed
    // metadata.tools); usage is metered at that chokepoint per resource
    // RETURNED, mirroring X's own read pricing.
    //
    // No `installable` here on purpose: x-search is a Direct-HTTP SEED
    // integration (integration-definitions.json), so the seed catalog already
    // makes it picker-visible. This registry entry exists ONLY to carry the
    // `premium` descriptor that `isPremiumDefinition` reads - which arms the
    // per-org opt-in gate, the priced acknowledgement modal, and the admin
    // pricing tab. See docs/adr/0034-x-search-platform-held-credential.md.
    id: 'x-search',
    name: 'X Search',
    category: 'social',
    description:
      'Read-only X (Twitter) search: recent public posts by keyword, hashtag, author, or any X search operator - for sentiment sweeps, topic monitoring, and trend research. Ninjafy manages X access for you - there is no key to enter.',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'x-search:read', name: 'Search Posts', description: 'Search recent public X posts (last ~7 days) via x_search_recent.', access: 'read' },
    ],
    docs_url: 'https://docs.x.com/x-api/posts/recent-search',
    beta: true,
    // ENG-7439: premium (billable). X bills us PER RESOURCE returned in a read
    // response ($0.005/post, $0.010/user via the app-only bearer), so the
    // integration is usage-priced and metered per resource at the broker
    // chokepoint. `meters` declares WHAT is metered + the physical unit; the
    // priced rate card (integration_rate_cards) holds the per-unit amount. Each
    // event_type must match what the broker writes to integration_usage_events.
    // Until a rate row is seeded an event prices at 0 (budget gate inert).
    premium: {
      pricing: 'usage',
      note: 'Billed per resource returned - each post and each unique author in a search result, mirroring X API read pricing.',
      meters: [
        { event_type: 'post_read', unit: 'post' },
        { event_type: 'user_read', unit: 'user' },
      ],
    },
  },
  {
    // Social Scraping: a PREMIUM, platform-managed integration on the Deck /
    // Firecrawl / X-Search model (ADR-0031, epic ENG-6920; ADR-0053). Backed by
    // Apify, but deliberately NOT surfaced as an "Apify" integration - the
    // integration is what a customer picks, and Apify is a vendor, not a job.
    // Splitting by job also means opt-in, rate card and monthly budget are keyed
    // per definition, so an org can take Local Business Data without taking the
    // pricier, more ToS-sensitive social scraping.
    //
    // Ninjafy holds ONE Apify account whose token (APIFY_API_TOKEN, read
    // from SSM - the API Lambda env is at the 4KB cap) every customer run bills
    // back to, so the key is a single platform-held secret, NOT a per-agent
    // credential. Auth type is `none` - customers never enter a key. Tools run on
    // a brokered-REST lane (apify-brokers.ts) so each run is metered at one
    // chokepoint and gated on a per-org monthly USD cap + the per-org premium
    // opt-in.
    //
    // "PPE-only" is guaranteed by a curated allowlist: the broker only ever runs
    // Actors on it, so a rental / pay-per-usage Actor can never surprise-bill the
    // shared account. (Apify's x402 rail enforces PPE-only at the payment layer;
    // we are on the account-key rail - Option A in the spike.) See
    // docs/design/apify-premium-option-a-ppe-allowlist.md.
    //
    // No `installable` here on purpose: this is a seed integration
    // (integration-definitions.json), so the seed catalog already makes it
    // picker-visible. This registry entry exists ONLY to carry the `premium`
    // descriptor that `isPremiumDefinition` reads - which arms the per-org opt-in
    // gate, the priced acknowledgement modal, and the admin pricing tab.
    id: 'social-scraping',
    name: 'Social Scraping',
    category: 'social',
    description:
      'Pull public posts and videos from TikTok, Instagram and YouTube - captions, media, engagement counts and URLs - for brand, competitor and campaign research. Ninjafy manages the scraping account for you; there is no key to enter.',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'social-scraping:scrape', name: 'Scrape Social Posts', description: 'Scrape public TikTok, Instagram and YouTube content (social_scrape_tiktok, social_scrape_instagram, social_scrape_youtube).', access: 'write' },
      { id: 'social-scraping:status', name: 'Check Scrape Runs', description: 'Poll a running scrape for status + results (social_scrape_status).', access: 'read' },
    ],
    docs_url: 'https://docs.apify.com/api/v2',
    beta: true,
    // Premium (billable). The upstream pay-per-event cost is per-Actor and
    // VARIABLE (each Actor's creator sets its own event prices), unlike X-Search's
    // fixed per-post price. So we meter the ACTUAL cost charged to us for each
    // run, as a pass-through: `units` = the run's `usageTotalUsd` in US dollars
    // (the physical unit `usd`), and the rate card prices each of those dollars at
    // a managed markup (1.5x). This keeps margin safe across cheap and expensive
    // scrapes alike. `event_type` must match what the broker writes to
    // integration_usage_events. Until the rate row is seeded a run prices at 0
    // (budget gate inert) - the rate-card migration ships in the same PR.
    //
    // The unit is US DOLLARS, not cents. Metering in cents priced the meter at
    // A$0.0225/unit, which rendered to the customer as "Actor Run (per usd_cent):
    // A$0.02" - jargon, and rounded 11% below the real rate because the shared
    // formatter only widens past 2dp below A$0.01. Per-dollar gives a whole-cent
    // price and a line a customer can reason about; the money billed is identical.
    premium: {
      pricing: 'usage',
      note: "Billed on the upstream pay-per-event cost of each scrape, at a managed markup. A 50-post TikTok scrape costs about A$0.19.",
      meters: [{
        event_type: 'actor_run',
        unit: 'usd',
        label: 'Scraping usage',
        unit_label: 'US$1 of scraping cost',
      }],
    },
  },
  {
    // Local Business Data: the second Apify-backed premium integration (ADR-0053).
    // Same platform-held account, same pass-through metering model, but a separate
    // definition so its opt-in, rate card and monthly budget are independent of
    // Social Scraping's - see the note on that entry for why the split exists.
    //
    // Distinct from Firecrawl on purpose: Firecrawl covers generic web
    // scrape/crawl/search, and we deliberately do NOT ship an Apify-backed
    // duplicate of that. This is the local-places job Firecrawl does not do.
    id: 'local-business-data',
    name: 'Local Business Data',
    category: 'knowledge',
    description:
      'Find local businesses on Google Maps and pull their public listing details - name, address, phone, website, category, rating and review count - for local market research, competitor lists and lead research. Ninjafy manages the data account for you; there is no key to enter.',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'local-business-data:search', name: 'Search Local Businesses', description: 'Search Google Maps for businesses and return their public listing details (local_business_search).', access: 'write' },
      { id: 'local-business-data:status', name: 'Check Searches', description: 'Poll a running search for status + results (local_business_search_status).', access: 'read' },
    ],
    docs_url: 'https://docs.apify.com/api/v2',
    beta: true,
    // Same cost pass-through metering as Social Scraping - see the note there for
    // why the unit is US dollars rather than cents.
    premium: {
      pricing: 'usage',
      note: "Billed on the upstream pay-per-event cost of each search, at a managed markup. A 20-place search costs about A$0.07.",
      meters: [{
        event_type: 'actor_run',
        unit: 'usd',
        label: 'Search usage',
        unit_label: 'US$1 of search cost',
      }],
    },
  },
  {
    // ENG-10854 / ADR-0110. The POLICY half of the catalog definition: this
    // registry is what `isPremiumDefinition` reads, so the premium descriptor
    // here is what makes the per-org opt-in required at all. The CUSTOMER-FACING
    // half - the seeded `integration_definitions` row that the Add Integration
    // picker lists - is deliberately NOT in this change. `/integrations/installs`
    // reads the DB catalog with a status filter; this registry is not a listing.
    //
    // The order matters and is not bookkeeping: `isOrgEntitledToPremium` returns
    // TRUE for a definition it does not know as premium, because a free
    // integration has no opt-in concept. So a broker shipped ahead of this entry
    // would serve an org that had never opted in. The broker additionally fails
    // closed on `isPremiumDefinition` being false, so the two halves cannot come
    // apart in that direction either.
    id: 'typed-evaluation',
    name: 'Typed Evaluation',
    category: 'knowledge',
    description:
      "Ask closed questions about a piece of text and get calibrated answers back — yes/no, pick-one, or a position on your own scale — with a probability for each. It never writes text and cannot answer outside the set of options you give it, which is what makes it safe to point at content you do not trust. Typical uses: is this message a repeat request, which category does this belong to, how urgent does this sound. Answers arrive in about half a second, so an agent can branch on one without spending a model turn.",
    // No customer credential exists: the call runs on Ninjafy's own gateway key.
    supported_auth_types: ['none'],
    capabilities: [
      {
        id: 'typed-evaluation:evaluate',
        name: 'Evaluate Text',
        description:
          'Ask closed questions about text the agent already holds and receive typed answers with probabilities (evaluate_text).',
        access: 'read',
      },
    ],
    beta: true,
    // ENG-8842's `included`, and the reason is the consent dialog rather than
    // the price. `pricing` drives the org admin's acknowledgement modal, where
    // `usage` renders as "manages and bills by usage" and `monthly` as a
    // subscription. Both would be false statements here: a call costs about
    // $0.000076 and nothing is metered or billed, so the opt-in exists to record
    // a decision about a SUB-PROCESSOR, not about money.
    //
    // ADR-0110 Decision 11 keeps the door open deliberately: if a genuinely
    // high-volume capability arrives later - bulk classification of a customer's
    // own corpus - it is priced at THAT capability's grain and this definition
    // stays `included`. Ninja Notes is the precedent (ENG-9278): it acquired a
    // metered capability and did not flip.
    premium: {
      pricing: 'included',
      note:
        'Typed evaluation is included in your plan — nothing is metered per question. Turning it on is a decision about data, not cost: the text an agent asks about is sent to Typesafe, through our own gateway, to be scored. It is never used to train a model and we store only the answers, never the text.',
    },
  },
  {
    // Ninja Notes (ADR-0063, ENG-8842). Meeting capture on the user's own Mac,
    // delivered to an agent as a tag-granted note the agent reads and summarises.
    //
    // No `installable` here, on purpose: ninjafy-notes is a Direct-HTTP SEED
    // integration (integration-definitions.json), so the seed catalog already
    // makes it picker-visible. This registry entry exists ONLY to carry the
    // `premium` descriptor that `isPremiumDefinition` reads — which arms the
    // per-org opt-in gate and the acknowledgement modal.
    //
    // PREMIUM BY ENTITLEMENT, WITH NO METER AND NO RATE CARD (ADR-0063 Decision
    // 9). Every other premium in this registry is priced because it costs the
    // platform something per call — a vendor bill, a model call, compute. Notes
    // costs S3 bytes: speech-to-text runs on the user's Mac, the platform never
    // summarises (Decision 3 gives that job to the agent, whose context is the
    // entire value), and a 45-minute meeting is tens of KB of text. The revenue
    // is the entitlement plus agent-hours — reading and summarising a note burns
    // the agent's turn, which is already billed, so metering the note as well
    // would charge for the same work twice through two mechanisms.
    //
    // What would change this: a per-note platform-side model call. The concrete
    // trigger is an AGENTLESS integration destination — "send this meeting
    // straight to Notion" has no agent to summarise it, so the platform must,
    // and that call is a real marginal cost. Expect a rate card WITH that slice,
    // scoped to that path, not applied to every note.
    //
    // ADR-0063 Decision 9 cites `agt-live` as the precedent for
    // entitlement-without-a-rate-card. It is not one: agt-live has no entry in
    // this registry at all, so `isPremiumDefinition('agt-live')` is false and it
    // is free on this axis. Its premium tier is `plans.live_premium` via
    // `getPlanForOrg` (ADR-0043) — a plan-tier entitlement enforced at the
    // CloudFront edge, a different mechanism that shares a word. Notes is the
    // first integration to need `pricing: 'included'`, which is why that value
    // now exists.
    id: 'ninjafy-notes',
    name: 'Ninja Notes',
    category: 'workspace-productivity',
    description:
      "Meeting notes captured on your Mac and sent to an agent. Ninja Notes records and transcribes a meeting on-device — the audio never leaves your machine — then hands the transcript to the agents you choose, which read it with your organization's context and write their own summary. Notes are private by default; sending one to an agent is the only thing that grants access.",
    supported_auth_types: ['none'],
    capabilities: [
      {
        id: 'ninjafy-notes:read',
        name: 'Read Notes',
        description:
          'List and read the meeting notes this agent has been sent (ninjafy_notes_list, ninjafy_notes_read).',
        access: 'read',
      },
      {
        id: 'ninjafy-notes:write-summary',
        name: 'Write Summaries',
        description:
          "Write or revise this agent's own summary of a note it was sent (ninjafy_notes_write_summary).",
        access: 'write',
      },
      {
        // ADR-0073. Present in the seed catalog's `defined_scopes` since
        // ENG-9358 but missing from this registry until now — a drift worth
        // noting, because this registry is what the org-scoped premium surface
        // reads and the seed is not.
        //
        // THE ONE `requires_org_opt_in` CAPABILITY TODAY, and the reason the
        // field exists. Live advising streams a meeting IN FLIGHT to a
        // server-side vet and meters per armed minute — a different exposure
        // from reading a transcript afterwards (ADR-0072), and a recurring
        // cost. Whether that happens is the customer's decision about their own
        // meetings, not ours; before ADR-0073 the only place to record it was
        // `live-meeting-advisor` in FLAG_REGISTRY, which is a staff control.
        id: 'ninjafy-notes:live-advise',
        name: 'Live Meeting Advice',
        // ENG-9278: THE PRICE IS QUOTED PER MEETING, NEVER PER HOUR. Nobody arms
        // an advisor for "an hour" — they arm it for a meeting — and the hourly
        // figure reads as a subscription for something billed only while it
        // runs. Both currencies are stated outright because the sub-dollar line
        // is USD-only; a spot conversion of either would not be a clean quote in
        // the other.
        //
        // These two figures are the rate card's ($0.03 USD / $0.05 AUD per armed
        // minute, migration 20260827000001) multiplied by thirty. They are prose
        // here rather than read from the rate card, which is a real drift
        // hazard — so it is held by a test that reprices both from the migration
        // and fails if they disagree, rather than by anyone remembering.
        description:
          'Let an agent advise on a meeting while it is happening. Audio is transcribed on the Mac as usual; short passages are then checked by Ninjafy to decide when the agent should speak up, and the agent sees those moments in real time. Off until you turn it on. Billed per minute the advisor is armed — about $0.90 for a half-hour advised meeting (A$1.50 in Australian dollars); a meeting you record without arming the advisor costs nothing.',
        access: 'write',
        requires_org_opt_in: true,
      },
    ],
    beta: true,
    // ENG-9278: STILL `included`, and the meter does not change that.
    //
    // Ninja Notes acquired its first metered path — live advising — and the
    // obvious move was to flip this to `usage`. It is refused. `pricing` drives
    // the org admin's acknowledgement modal, where `usage` renders as "manages
    // and bills by usage"; saying that to the many orgs who record notes and
    // never arm an advisor would be a false statement in a consent dialog, which
    // is the exact harm the `included` case was added to prevent, inverted.
    //
    // Notes really are included. The one metered thing is a capability with its
    // own org opt-in and its own price in its own description, which is where
    // the consent for it actually happens.
    //
    // The cost of this choice is that the rate card is not rendered on the
    // premium surface (that reads definition-level meters, and only for
    // `pricing: 'usage'`), so the price is prose in two places. Pricing the
    // CAPABILITY instead — meters on the capability descriptor, read by the
    // premium surface — is the structurally right answer and the follow-up.
    premium: {
      pricing: 'included',
      // "THE ONE EXCEPTION" BECAME TWO (ENG-9913). Cloud transcription is the
      // second metered capability under this integration, and this sentence was
      // true only for as long as its flag stayed off — it would have become
      // false on arming, silently, in a consent dialog. Both exceptions are
      // written the same way, and both are still off until someone turns them
      // on, so the sentence is accurate before and after the rollout.
      //
      // "Per hour of AUDIO" is deliberate here too: the two capture sources post
      // independently, so an hour-long meeting on default settings spends two
      // audio-hours (ADR-0063 §2a). The Mac's own caption carries the fuller
      // explanation, at the point where the person choosing it can read it.
      note:
        'Notes are included in your plan. Transcription runs on your own Mac by default and no platform summariser runs, so nothing is metered per note — the agent turn that reads it is billed as agent-hours like any other. Two capabilities are turned on separately and billed when they are: live meeting advising, per minute the advisor is armed; and cloud transcription, per hour of audio.',
    },
  },
  {
    // Seed-catalog toolkit (composed by the ultimate-app-coder bundle). Registered
    // here only so the manager's ensureToolkitCli installs the binary via the
    // failure-surfacing cli_tool path (not a customer-installable native -> no
    // `installable`). Single source for the install; the seed integration no
    // longer carries an on_install for it.
    id: 'greenlight',
    name: 'Greenlight (App Store Compliance)',
    category: 'code',
    description:
      'Apple App Store pre-submission compliance scanner by Revyl - scans app source, privacy manifests, binaries, and App Store Connect status for App Review rejection risks',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'greenlight:scan', name: 'Scan for compliance', description: 'Scan app source, privacy manifests, and binaries for App Store rejection risks', access: 'read' },
    ],
    cli_tool: {
      package: 'greenlight',
      binary: 'greenlight',
      env_key: '',
      // Revyl greenlight is a Go binary. Prefer the Homebrew tap (macOS, and
      // Linuxbrew when present); fall back to `go install` where Go is on the
      // host. Exits non-zero when neither is available so the manager records
      // the failure instead of masking it.
      installer: 'script',
      script:
        'if command -v brew >/dev/null 2>&1; then brew install revylai/tap/greenlight; elif command -v go >/dev/null 2>&1; then go install github.com/RevylAI/greenlight/cmd/greenlight@latest; else echo "greenlight: no supported installer (need brew or go)" >&2; exit 1; fi',
    },
    docs_url: 'https://github.com/RevylAI/greenlight',
  },
  {
    // Seed-catalog toolkit (composed by the ultimate-app-coder bundle). Registered
    // here only for the cli_tool install path; not a customer-installable native.
    id: 'expo',
    name: 'Expo (EAS CLI)',
    category: 'code',
    description:
      'Expo Application Services (EAS) CLI - build, submit, and update iOS and Android apps in the cloud',
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'expo:read', name: 'Read builds', description: 'Read EAS build and update status and history', access: 'read' },
      { id: 'expo:ship', name: 'Build and ship', description: 'Trigger cloud builds, submit to the app stores, and publish OTA updates', access: 'write' },
    ],
    cli_tool: {
      package: 'eas-cli',
      binary: 'eas',
      env_key: 'EXPO_TOKEN',
      installer: 'npm',
    },
    docs_url: 'https://docs.expo.dev/eas/',
  },
  {
    id: 'custom',
    name: 'Custom Integration',
    category: 'custom',
    description: 'Connect to any service via API key or webhook',
    supported_auth_types: ['api_key', 'webhook', 'none'],
    capabilities: [
      { id: 'custom:api-access', name: 'API Access', description: 'Generic API access with configured credentials', access: 'read' },
    ],
  },
] as const;

const integrationMap = new Map<string, IntegrationDefinition>(
  INTEGRATION_REGISTRY.map((i) => [i.id, i]),
);

export function getIntegration(id: string): IntegrationDefinition | undefined {
  return integrationMap.get(id);
}

/**
 * ADR-0073: the capabilities of `definitionId` an organization must explicitly
 * opt in to before they may be used — `requires_org_opt_in` capabilities, in
 * declaration order.
 *
 * THE ONE ENUMERATION. Both the opt-in surface (which rows to render) and the
 * resolver (which id to look up) read it, so neither can invent a feature the
 * other has never heard of, and an unknown scope id arriving from a client is
 * rejected against this list rather than trusted. Empty for every integration
 * that has none, which is all but one today — callers should treat an empty
 * result as "nothing to ask about", never as a lookup failure.
 *
 * Returns a fresh array; the registry's own objects are shared, so callers must
 * not mutate the capability records themselves.
 */
export function optInCapabilitiesFor(definitionId: string): IntegrationCapability[] {
  return (getIntegration(definitionId)?.capabilities ?? []).filter((c) => c.requires_org_opt_in === true);
}

/**
 * ADR-0073: is `scopeId` a declared opt-in capability of `definitionId`?
 *
 * The validation every write path owes itself. `org_integration_feature_opt_ins`
 * stores `scope_id` as free text (the same choice `definition_id` makes, and for
 * the same reason — the row must not depend on catalog seeding), so nothing at
 * the storage layer stops a typo, a renamed scope, or a client-supplied id that
 * names no capability at all from being written. A row like that is worse than
 * no row: it reads as a deliberate customer decision, it survives, and the
 * capability it appears to govern is not the one anybody meant.
 */
export function isOptInCapability(definitionId: string, scopeId: string): boolean {
  return optInCapabilitiesFor(definitionId).some((c) => c.id === scopeId);
}

export function getAllIntegrationIds(): IntegrationId[] {
  return INTEGRATION_REGISTRY.map((i) => i.id);
}

/**
 * ENG-8804: true when this id names a PLATFORM-INTERNAL integration — one
 * Ninjafy attaches at provisioning or by central policy, which an agent
 * must never be offered and must never be able to request. See
 * {@link IntegrationDefinition.platform_internal} for why this is an explicit
 * flag rather than something inferred from `installable` or the auth types.
 *
 * False for any id the in-code registry does not know, which is the correct
 * answer for the DB `toolkit_definitions` namespace: every Composio/native
 * toolkit row there is customer-facing by construction.
 */
export function isPlatformInternalIntegration(id: string): boolean {
  return integrationMap.get(id)?.platform_internal === true;
}

/**
 * The ids {@link isPlatformInternalIntegration} matches. Derived from the
 * registry rather than hand-listed, so marking a fourth entry needs no second
 * edit — a hand-kept list is how the two native picker lists drifted before
 * ENG-7015 folded them into one.
 */
export const PLATFORM_INTERNAL_INTEGRATION_IDS: readonly IntegrationId[] =
  INTEGRATION_REGISTRY.filter((d) => d.platform_internal === true).map((d) => d.id);

/**
 * ENG-7015: a customer-installable NATIVE integration, flattened to the shape
 * the Add Integration picker consumes. This is the single source of truth both
 * the webapp picker (`STATIC_INTEGRATION_OPTIONS`) and the API org allowlist
 * (`NATIVE_PICKER_INTEGRATIONS`) now derive from, so the curated native list is
 * defined exactly once.
 */
export interface InstallableNativeIntegration {
  id: IntegrationId;
  name: string;
  /** Display category label (e.g. "Code"), not the runtime category slug. */
  category: string;
  /** Auth options the connect UI offers (see InstallablePickerMeta.authTypes). */
  authTypes: IntegrationAuthType[];
  beta?: boolean;
}

/**
 * The curated set of customer-installable native integrations, derived from the
 * registry entries that carry an `installable` descriptor. Order follows the
 * registry; both consumers sort by name for display, so it is not significant.
 */
export const INSTALLABLE_NATIVE_INTEGRATIONS: readonly InstallableNativeIntegration[] =
  INTEGRATION_REGISTRY.filter(
    (d): d is IntegrationDefinition & { installable: NonNullable<IntegrationDefinition['installable']> } =>
      d.installable != null,
  ).map((d) => ({
    id: d.id,
    name: d.name,
    category: d.installable.category,
    authTypes: d.installable.authTypes,
    ...(d.beta ? { beta: true } : {}),
  }));

/** Just the ids of {@link INSTALLABLE_NATIVE_INTEGRATIONS} — the API allowlist's input. */
export const INSTALLABLE_NATIVE_INTEGRATION_IDS: readonly IntegrationId[] =
  INSTALLABLE_NATIVE_INTEGRATIONS.map((i) => i.id);
